export const helpTopics = [
  { id: "start", label: "Getting started" },
  { id: "fields", label: "Fields & forms" },
  { id: "configuration", label: "Configuration" },
  { id: "design", label: "Document design" },
  { id: "proof", label: "Proof & export" },
  { id: "bulk", label: "Bulk generation" },
  { id: "workspace", label: "Workspace & access" },
  { id: "integration", label: "Integration" },
] as const;
type TopicId = (typeof helpTopics)[number]["id"];
type Destination =
  | { label: string; kind: "module"; module: string }
  | {
      label: string;
      kind: "dialog";
      dialog: "new" | "import-document" | "history" | "settings";
    }
  | { label: string; kind: "impersonation" };
export const helpActions = {
  new: { label: "Create a project", kind: "dialog", dialog: "new" },
  import: {
    label: "Import a document",
    kind: "dialog",
    dialog: "import-document",
  },
  history: { label: "Open Draft history", kind: "dialog", dialog: "history" },
  settings: {
    label: "Open Project settings",
    kind: "module",
    module: "settings",
  },
  overview: { label: "Open Overview", kind: "module", module: "overview" },
  fields: {
    label: "Open Fields & variables",
    kind: "module",
    module: "configuration",
  },
  designer: {
    label: "Open Document designer",
    kind: "module",
    module: "designer",
  },
  proof: { label: "Open Live proof", kind: "module", module: "proof" },
  profiles: { label: "Open Your profiles", kind: "module", module: "profiles" },
  globalProfiles: {
    label: "Open Configuration → Global Profiles",
    kind: "module",
    module: "resources-profiles",
  },
  impersonation: {
    label: "Open Impersonation (new tab)",
    kind: "impersonation",
  },
  bulk: { label: "Open Bulk generation", kind: "module", module: "bulk" },
  configuration: {
    label: "Open Configuration",
    kind: "module",
    module: "resources",
  },
  assets: {
    label: "Open Configuration → Assets",
    kind: "module",
    module: "resources-assets",
  },
  fonts: {
    label: "Open Configuration → Fonts",
    kind: "module",
    module: "resources-fonts",
  },
  forms: {
    label: "Open Configuration → Forms",
    kind: "module",
    module: "resources-forms",
  },
  datasets: {
    label: "Open Configuration → Datasets",
    kind: "module",
    module: "resources-datasets",
  },
  mapping: {
    label: "Open Configuration → Mapping",
    kind: "module",
    module: "resources-mapping",
  },
  outputs: {
    label: "Open Configuration → Output profiles",
    kind: "module",
    module: "outputs",
  },
  setup: { label: "Open Workspace setup", kind: "module", module: "setup" },
  access: { label: "Open Users & roles", kind: "module", module: "access" },
  operations: {
    label: "Open Render operations",
    kind: "module",
    module: "operations",
  },
  integrations: {
    label: "Open Integrations",
    kind: "module",
    module: "integrations",
  },
} as const satisfies Record<string, Destination>;
export type HelpActionId = keyof typeof helpActions;
export interface HelpGuide {
  id: string;
  topic: TopicId;
  title: string;
  summary: string;
  keywords: string[];
  steps: [string, string][];
  options?: [string, string][];
  notes?: string[];
  related: string[];
  actions: HelpActionId[];
}

export const helpGuides: HelpGuide[] = [
  {
    id: "bird-integrations",
    related: ["application-accounts"],
    actions: ["integrations"],
    topic: "integration",
    title: "Connect Bird email and messaging",
    summary:
      "Configure regional Bird connections and inspect provider responses.",
    keywords: [
      "Bird",
      "MessageBird",
      "email",
      "SMS",
      "WhatsApp",
      "voice",
      "verification",
      "contacts",
      "webhooks",
    ],
    steps: [
      [
        "Connect application email",
        "Open Administration → Email providers, choose Bird.com, select the region matching your API key, and enter the verified sender. Save, test and assign the connection. Email delivery logs contain the provider status, error and message identifier; acceptance does not confirm inbox delivery.",
      ],
      [
        "Enable other services",
        "Open Administration → Bird integrations. Choose the administration scope, name the connection, select the region, enter the API key and enable the operations it may use. Keys are stored encrypted and are never returned to the browser.",
      ],
      [
        "Run and inspect a request",
        "Select a saved connection and an operation. Reading the workspace checks connectivity without sending a message. For sending or creating records, enter the request fields from Bird’s API reference and explicitly confirm. The response includes the HTTP status and sanitized provider result.",
      ],
    ],
    notes: [
      "Bird account permissions and enabled services still apply. Application login verification continues to use the configured email provider; the Bird Verify operation is a separate integration tool.",
    ],
  },
  {
    id: "merge-output-profiles",
    related: [],
    actions: ["outputs"],
    topic: "configuration",
    title: "Merge common output profiles",
    summary:
      "Review identical shared definitions and keep one catalog entry without changing saved document outputs.",
    keywords: ["merge", "duplicate", "output", "global profile", "archive"],
    steps: [
      [
        "Find matching profiles",
        "Open Configuration → Output profiles and choose Find common profiles. Active shared profiles with the same format, color space, output role and production or sheet references are grouped. Open Compare group to see differences and select merged settings. Higher DPI and image quality are recommended first; existing document snapshots remain unchanged.",
      ],
      [
        "Choose the survivor",
        "Select the groups to merge and choose the profile to retain. A workspace default is always retained. Different settings and changed versions cannot be merged.",
      ],
      [
        "Review and merge",
        "Choose Review merge, check the survivor names and duplicate count, then confirm. Duplicate catalog entries are archived together. Existing project assignments, draft history and published snapshots retain their exact saved settings and versions.",
      ],
    ],
  },
  {
    id: "public-access-requests",
    related: ["application-accounts"],
    actions: ["access"],
    topic: "workspace",
    title: "Request and review workspace access",
    summary: "Use the public Proof Studio home page to request an invitation.",
    keywords: [
      "signup",
      "request access",
      "marketing",
      "home",
      "workspace",
      "company",
    ],
    steps: [
      [
        "Open the public website",
        "Select the Proof Studio logo in the top-left corner or visit /welcome. Help and API documentation are public. Submit your name, email, company and a short description to request access.",
      ],
      [
        "Review as an administrator",
        "Global administrators open Administration → Access requests. Prepare invitation copies the person’s details into the existing invitation form. Select their workspace and role before creating a private setup link or sending an invitation. A request alone never grants access.",
      ],
      [
        "Switch workspaces",
        "Use Workspace (company) at the top of the sidebar. It lists the same accessible site/company combinations as the previous header selector.",
      ],
    ],
  },

  {
    id: "application-accounts",
    topic: "workspace",
    title: "Set up accounts, invitations and two-factor access",
    summary:
      "Unlock first-run setup, create your administrator, choose demo data and configure global or site email.",
    keywords: [
      "initial configuration",
      "access token",
      "administrator",
      "invitation",
      "SparkPost",
      "Gmail",
      "Google",
      "authenticator",
      "2fa",
      "TOTP",
      "site",
      "company",
    ],
    steps: [
      [
        "Complete initial setup",
        "Read the one-use setup-access.token privately from the server data directory's application-accounts folder. Enter it on the setup screen, create the administrator and first site/company, choose demo data, configure email or explicitly defer it, then finish. Existing projects are retained.",
      ],
      [
        "Manage your organization",
        "Use Administration in the top account navigation. Global administrators manage all sites and companies. Site administrators manage every company in their site; company administrators manage their company. Switch the active site/company using Workspace (company) in the sidebar.",
      ],
      [
        "Connect email",
        "In Administration > Email providers, select the application or site scope. Add SparkPost or Gmail and assign the default. Sites inherit application email unless they choose their own connection. Send a test to your signed-in address before inviting users.",
      ],
      [
        "Invite people",
        "Open People & invitations, select the scope and role and send an invitation. The emailed return link expires after 72 hours and works once. Recipients choose a password; existing accounts confirm their current password. Revoke unused invitations from the list.",
      ],
      [
        "Connect Google",
        "A global administrator configures Google sign-in with OAuth credentials and the displayed callback URL. Existing users then open the account menu (their name at the bottom of the navigation) > Account & security > Connect Google account. Google sign-in only accepts explicitly linked identities; matching an email alone does not link accounts.",
      ],
      [
        "Require two factors",
        "Administration > Two-factor policies sets allowed methods and requirements globally, per site or per company. Children cannot weaken a parent. Users enroll email codes or scan an authenticator QR in Account & security. Save the one-use recovery codes privately. Required verification blocks document access until completed.",
      ],
    ],
    notes: [
      "Gmail cannot send with an ordinary API key. Choose Google OAuth, a Workspace service-account JSON key with administrator-approved domain delegation, or a Gmail app password. The email editor links to the required credential pages. Real delivery requires your own verified sender and provider credentials.",
      "Account & security includes password changes, active sessions and sign-out controls. Forgot password emails a 30-minute reset link and preserves enrolled two-factor protection. Keep the private account encryption key with backups; losing it makes saved provider credentials and authenticator secrets unavailable.",
      "Host identity and infrastructure configuration remain explicit deployment modes. Their host tokens and operator login are separate from application-owned account sign-in.",
    ],
    related: ["workspace-setup", "shared-workspace", "access"],
    actions: ["setup"],
  },
  {
    id: "field-policies",
    topic: "configuration",
    title: "Validation and formatting presets",
    summary:
      "Choose standard rules and formats, or create reusable overrides for a site, company, project or variable.",
    keywords: [
      "validation",
      "formatting",
      "date",
      "time",
      "number",
      "currency",
      "website",
      "URL",
      "presets",
      "scope",
      "regex",
      "custom message",
      "locale",
      "minimum",
      "maximum",
    ],
    steps: [
      [
        "Choose shared defaults",
        "Open Configuration → Validation presets or Formatting presets. Application presets are read only; use one as a starting point for a custom scoped version.",
      ],
      [
        "Create a scoped version",
        "Set a name, stable preset key and allowed scope. A more specific preset with the same key takes precedence: variable, project, company, site, application. Project configuration includes the current project and optional variable scope.",
      ],
      [
        "Test custom settings",
        "Date/time uses Unicode tokens such as yyyy-MM-dd and HH:mm. Numbers use skeletons such as #,##0.00, 0.0% or ¤#,##0.00 with a currency code. Test a value, apply valid settings and save the version.",
      ],
      [
        "Assign to a variable",
        "In Fields & variables, open the Validation or Formatting section, select a preset or apply custom settings. Only rules and formatters that fit the control type are offered: character limits for text, value ranges for numbers and selection counts for checkbox groups. Assignment copies a version into that variable; later library edits do not change it. Parts retain only explicit overrides.",
      ],
      [
        "Use Custom Regex and custom messages",
        "Choose Custom Regex, enter an expression and pick Value must match or Reject values containing a match. Under Custom messages, $0 inserts the entered value, $1–$9 insert capture groups (Reject mode only) and $$ is a dollar sign. Apply and Save stay disabled until the expression and every message reference are valid.",
      ],
    ],
    notes: [
      "Rules are no longer inferred from a variable's name: choose Website URL explicitly to validate web addresses. This checks URL syntax, not whether the website is online. Saved Automatic settings still load and apply only ZIP, URI and phone control semantics.",
      "Formatting locale defaults to User's Browser, so each viewer sees their own conventions; choose a specific locale for fixed output. Images offer no formatting, and number inputs are right-aligned.",
      "Date/time values remain ISO and numbers remain raw decimal values; formatting affects the proof and generated document. Invalid calendar dates, times and numeric inputs are rejected. Date/time formatting uses UTC, independent of the renderer machine.",
      "Cross-company site sharing requires a trusted host scope resolver. Without one, the tenant is its own isolated site and company; configured accounts cannot claim another site through request data.",
      "Profile collection Quick setup uses the full variable designer; Advanced setup uses the compact field list. Switching retains the same definition.",
    ],
    related: ["variables", "part-overrides"],
    actions: ["fields", "configuration"],
  },
  {
    id: "portable-project",
    topic: "integration",
    title: "Move a project with its images and fonts",
    summary:
      "Transfer the complete native project or export editable IDML and publishing projects with linked artwork and desktop fonts.",
    keywords: [
      "portable",
      "package",
      "ZIP",
      "export",
      "backup",
      "fonts",
      "resources",
      "IDML",
      "InDesign",
      "publishing",
      "composition",
    ],
    steps: [
      [
        "Verify the resources",
        "Open Integrations → Portable project package. The application verifies managed images and fonts and lists any destination host dependencies before download.",
      ],
      [
        "Download the ZIP",
        "Choose Download portable ZIP. The package includes the native project, a versioned manifest, resource bytes and SHA-256 integrity hashes. Variables, sparse part overrides, forms, rules, datasets, presets, editable layout, document frames and output profiles remain in the native snapshot.",
      ],
      [
        "Restore in the destination",
        "Use Import document and select the ZIP. Import validates the manifest, hashes, native settings and decoded resources before writing resources. It creates a new draft in the current tenant and preserves variable and layer IDs. It does not replace an existing project or publication.",
      ],
      [
        "Choose editable IDML",
        "Choose Editable InDesign IDML (preview) and optionally a document part. Review the conversion report, then download and extract the ZIP. Open Target/Document.idml with its adjacent Links and Document fonts folders. Text, paths and image frames remain editable; supported text bindings become shared custom text variables. The full native project is retained separately in the same package.",
      ],
      [
        "Choose an editable publishing project",
        "Choose Editable publishing project (preview), optionally select a part and review the conversion report. Extract the ZIP and keep Target together. Make the packaged Fonts available to the target font manager and open Project.pf. Supported variables, conditional rules and visibility stay dynamic. Supported top-aligned text frames retain authored copyfit limits, shared reductions and content-height ceilings. Explicit paragraph spacing and tab intervals stay editable, and rich inputs retain formatted content. Review per-frame limitations, individual PDF fallbacks and text shaping before production.",
      ],
      [
        "Reconnect host services",
        "Configure destination directory, DAM, map, dynamic-source and form-profile providers as required. Server provider connections, runtime records, approved proof packages and job history are managed separately.",
      ],
    ],
    notes: [
      "Up to 50 MB per package and 8 MB per managed font face. Missing resources block packaging. Legacy source records and dataset rows are included; treat the ZIP as project data. Hashes detect changed bytes and are not a signature of the sender.",
      "IDML is a preview export: native forms, rules, datasets, live formatting, crop shapes and advanced composition do not all have target equivalents. Review the item warnings and check the result in InDesign. Native InDesign opening is not yet qualified. Editable publishing project (preview) includes Project.pf, Layout.xdt, Layout.tsl, Sample.jdt, fonts, graphics and safe generated formatters. Keep Target together and open Project.pf after making the packaged fonts available in the target font manager. Supported text and image variables, conditional rules and visibility remain dynamic. Unsupported items have individual PDF fallbacks and explicit warnings. Native target application opening and compositor fidelity remain under qualification. PDF, SVG and raster artwork exports are available through Live proof.",
      "Importing the complete unchanged ZIP restores the native snapshot. To bring back target edits, choose Import revision and set Package content to Edited IDML project ZIP. Repack Target with its edited IDML, links and fonts; keep Native, Resources, Baseline and the manifest unchanged. Compare and accept individual changes before applying and saving. Publishing ZIPs restore the complete native snapshot unchanged. For a changed publishing target, ZIP the contents of Target separately and use Import revision; native reconciliation of edited publishing packages is not supported yet.",
    ],
    related: ["import-document", "part-overrides", "embedding"],
    actions: ["integrations", "import"],
  },
  {
    id: "first-document",
    topic: "start",
    title: "Create your first document",
    summary:
      "Go from a starter or blank design to a personalized, downloadable proof.",
    keywords: ["quick start", "new project", "demo", "database"],
    steps: [
      [
        "Choose a starting point",
        "Open Projects → New project. Select a starter to explore a working example, or choose Blank document to build your own.",
      ],
      [
        "Define the form",
        "In Fields & variables, add the inputs you need, set defaults and validation, and organize them into groups and tabs.",
      ],
      [
        "Connect the artwork",
        "In Document designer, add layers and bind them to variables. Use Live proof to enter values and review every page.",
      ],
      [
        "Save and generate",
        "Save draft to retain configuration changes. Choose an output profile in Live proof, generate the document, and download the completed artifact.",
      ],
    ],
    options: [
      [
        "Project navigation",
        "Projects lists every project. Open one to see its pages (Overview, Fields & variables, Document designer, Live proof and more) nested under it in the sidebar, and follow the breadcrumb (Workspace › Projects › project › page) back at any time. Unsaved edits are saved before you switch or return to Projects. On small screens, Project configuration and Configuration open dismissible menus. Configuration is the shared catalog; Project configuration manages assignments for the open project.",
      ],
      [
        "Starter examples",
        "Invitations, business cards, transparent artwork, flowing newsletters, directory/profile cards and maps demonstrate different workflows.",
      ],
      [
        "No database",
        "The demo uses browser storage and local API services. You can edit the starters without connecting an external system.",
      ],
    ],
    related: ["variables", "layers", "live-proof"],
    actions: ["new", "overview"],
  },
  {
    id: "statements",
    topic: "fields",
    title: "Create reusable statement groups",
    summary:
      "Choose versioned text blocks, keep part defaults sparse, and switch between reusable statements and custom text.",
    keywords: [
      "statement",
      "statement group",
      "reusable text",
      "paragraph",
      "multiline",
      "statement version",
      "STATEMENT_ID",
    ],
    steps: [
      [
        "Create a group",
        "Open Fields & variables → Rules & presets, or Configuration → Datasets → Document datasets. Add statement group creates Statement ID, Name and multiline Content columns. Edit rows and publish a dataset version. Reusable dataset resources and reviewed uploads work with the same tables.",
      ],
      [
        "Bind a variable",
        "For a text, textarea or rich-text variable, choose Source → statement. Select the dataset version, content/label columns and a default statement. Any / literal default leaves the ordinary default available. A part can override only its source; Inherit restores the project binding.",
      ],
      [
        "Personalize the text",
        "In Live proof, use Statement for … to fill the field. Editing the text makes it custom content. Read-only and system variables keep their authoritative defaults. Try the Reusable statement letter starter without a database.",
      ],
      [
        "Save, export or generate in bulk",
        "Native saved proofs retain statement identity and its exact group version. Download statement selection exports JSON for a form-matched bulk column; plain custom text also works. Stale or altered selection claims fail validation. Legacy customization/XML exports contain actual text, with metadata kept in native sidecars.",
      ],
    ],
    notes: [
      "The packaged legacy group/statement tables do not include assembled content or complete piece membership. Hosts can supply approved text explicitly, or bind an imported variable to a native statement dataset. No statement scripts or expressions are executed.",
    ],
    related: ["datasets", "part-overrides", "live-proof", "embedding"],
    actions: ["fields", "datasets", "proof"],
  },
  {
    id: "project-revisions",
    topic: "start",
    title: "Review and merge a new document revision",
    summary:
      "Import an updated document, match its items, choose individual changes and preview the merged draft.",
    keywords: [
      "revision",
      "merge",
      "update",
      "differences",
      "matching",
      "history",
      "version",
    ],
    steps: [
      [
        "Import the revision",
        "Choose Import revision for the current document. Upload project JSON or a supported document format. The current draft is kept while the new source is parsed; review any import warnings.",
      ],
      [
        "Match existing items",
        "Review suggestions based on IDs, source identifiers and unique names. Choose a current item or New item. Resolve ambiguous matches explicitly. Reset matches or choose a different source when needed; changing matches resets change selections.",
      ],
      [
        "Choose the changes",
        "Every difference starts with Keep current. Accept individual property edits, additions, removals and ordering changes. Search or filter variables, pages, layers, sparse part patches, fonts, forms, datasets, rules and output profiles. Expand a change to compare values.",
      ],
      [
        "Preview and apply",
        "Review default-value artwork for the current and merged documents. Select a page and adjust zoom from 25% through 200%. New pages are identified explicitly. Missing dependencies block preview until compatible changes are selected together. Images and fonts are checked across every page; Retry resource check repeats a failed check. Download the complete review report, then Apply to draft. Applying creates one undo step. A draft changed during import or review requires restarting the comparison.",
      ],
      [
        "Save the reviewed draft",
        "Check Live proof and choose Save draft. The existing project ID is retained, saved draft history follows the tenant retention policy, and published versions remain immutable. If the draft changed during review, start a new comparison.",
      ],
    ],
    notes: [
      "Part overrides remain authored deltas; inherited project values are not copied into them. Array properties such as dataset rows and choices are reviewed together. The review report contains project values.",
      "Edited IDML package mode compares Target against the immutable export baseline while retaining native configuration. Plain variable edits update the project default or the selected part's sparse default override. Edited calculated results create literal overrides and keep the original calculations. Text snapshots and fixed placements are identified in the import notes. Unsupported typography/effects and ambiguous origins block import for native review.",
    ],
    related: [
      "import-document",
      "portable-project",
      "part-overrides",
      "live-proof",
    ],
    actions: ["overview", "designer", "proof"],
  },
  {
    id: "import-document",
    topic: "start",
    title: "Import PDF, InDesign, publishing packages or image artwork",
    summary:
      "Create a new document with generated variables and editable layers from existing artwork.",
    keywords: [
      "PDF",
      "IDML",
      "INDD",
      "INDT",
      "ZIP",
      "publishing project",
      "PNG",
      "JPEG",
      "WebP",
      "TIFF",
      "variable names",
      "confidence",
      "OCR",
    ],
    steps: [
      [
        "Import from Projects",
        "Choose Import document and select a PDF, IDML, INDD/INDT, publishing-project ZIP, PNG, JPEG, WebP or TIFF file.",
      ],
      [
        "Import a collection of projects",
        "Choose Project archive (ZIP) for a ZIP containing dozens of project folders beneath a root folder. Uploading only lists candidates. Select projects and any publishing layouts, then choose Import selected projects. Each selection becomes a separate draft. Failures are reported per project; successful imports stay saved. View imported projects returns to Projects. Closing the review without importing adds nothing.",
      ],
      [
        "Review the import report",
        "Inspect extracted items, inferred variable keys, naming confidence, font substitutions and missing links. Download the report if you need a record.",
      ],
      [
        "Refine the draft",
        "Review variables in Fields & variables, compare the artwork in Document designer with the original, replace low-resolution image previews, and save the draft.",
      ],
    ],
    options: [
      [
        "Smarter variable names",
        "Explicit <Variable_Name> tokens become shared text variables, including repeated placements on different pages. Multiple tokens in one frame retain their individual bindings and original formatting. Authored names, nearby PDF labels, recognizable email/phone/date content and text summaries suggest other keys. Review the report and delimiter notes before publishing.",
      ],
      [
        "Multiple variables in a text layer",
        "Select the layer in Document designer and choose Use multiple variables. Add text or variable fragments, rebind variables and reorder fragments. Expand Text and formatting to edit literal content. Values are inserted as text within the layer's formatting. Use static text freezes field defaults, while Linked variable restores a single binding.",
      ],
      [
        "INDD and INDT",
        "A bundled converter first produces IDML. Missing links and conversion warnings remain visible in the report.",
      ],
      [
        "Publishing-project ZIP preview",
        "A package with one project and one layout imports declared variables, conditional fragments, managed resources, supported literal-text formatters, nested layout groups and print settings. Source paths identify supplied files; missing images need replacements. Source scripts never execute. Complete copyfit, paragraph options and exact output matching remain under qualification: review the report and artwork before using the draft.",
      ],
      [
        "Large archive uploads",
        "ZIP and ZIP64 collections support up to 8 GiB, uploaded in 8 MiB chunks. Resume upload recovers a failed transfer while the dialog remains open. Projects import one at a time and keep the individual 50 MB compressed / 150 MB expanded / 2,000-file limits. Backup and generated folders are skipped. Cancel stops pending work and keeps completed drafts. Staged bytes expire after 24 hours and are removed when the dialog closes when possible.",
      ],
      [
        "Calculated text layers",
        "Imported composed text uses the same bounded calculation engine as variables. In Document designer, select the layer and expand Calculated text to edit its expression. Use static text freezes the current defaults. Linked variable replaces the expression with one binding. Rich text from ordinary inputs is escaped before insertion.",
      ],
      [
        "Text-frame placement",
        "Select an imported text layer in Document designer. First text baseline chooses Font size or Font ascent; Vertical text alignment also offers Bottom baseline for source frames whose last baseline sits on the bottom edge. The importer retains supported source alignment and leading. Managed font resources resolve source family/style names to exact PostScript faces; mixed text spans retain their own faces.",
      ],
      [
        "Linked PDF artwork",
        "The independent import module accepts linked PDF bytes, keeps a cropped single-page PDF for original-color vector export, and creates a transparent preview at up to 600 DPI. Content-cropped artwork retains its authored bounds. Changing the image or its adjustments uses the new pixels. The upload dialog does not read an adjacent folder automatically; review missing links and supply replacement assets.",
      ],
      [
        "Print and trim profiles",
        "Imports create Preview / Proof (96 DPI), Proof Download (72 DPI), High Res (300 DPI) and Print (600 DPI). These resolution defaults are editable. Matching detected bleed profiles are reused; missing profiles are created in Configuration → Page bleed profiles. PDF print boxes and IDML bleed/slug preferences supply page settings, including rotated or facing-page edges. Review the import report. Existing PDF marks stay source artwork. In Output profiles, Include page bleed, slug and marks controls each export. Page settings can clip artwork to the bleed boundary or allow it into the outer media margin.",
      ],
      [
        "Source ink and text overhang",
        "Imported IDML text allows ink overhang when its baseline fits the frame. Disable Allow glyph overhang to clip that ink. Preserve imported CMYK paint keeps supported original channels and tints in PDF without output conversion; edited colors use their new values. This does not qualify PDF/X or press matching.",
      ],
      [
        "Text shaping and frame edges",
        "Text kerning and Text ligatures can keep the imported/font default or explicitly turn the feature on or off for a layer. Custom kerning pairs lets you add spacing adjustments in 1/1000 em, optionally scoped to one managed font; Apply kerning pairs saves the edits. Positive values add space and negative values tighten it. Pairs apply between separate glyphs inside words; disable ligatures to adjust letters that otherwise form one glyph. Text tab stops provides a Tab interval (pt) from 0.01 to 14400 points; Apply tab interval saves it, Reset tab draft restores the saved value, and Remove tab override restores imported paragraph settings. Leading and repeated tabs use the next fixed stop, shared by preview and export. Preview, wrapping and native exports use the same settings. Positive Text baseline shift (pt) moves the ink upward without changing frame height. Disable Allow glyph overhang to clip text ink; image layers offer Clip image to frame. Native ZIP packages retain all settings; review IDML conversion issues for custom kerning, tab intervals and baseline adjustments.",
      ],
    ],
    notes: [
      "Scanned PDF pages become images; OCR is not included. Outlined text remains artwork. Advanced InDesign effects and some PDF features may need manual reconstruction.",
    ],
    related: ["base-shape", "managed-fonts", "variables"],
    actions: ["import"],
  },
  {
    id: "save-versions",
    topic: "start",
    title: "Save, publish, restore and back up",
    summary:
      "Keep draft history, publish immutable versions, and back up the browser workspace.",
    keywords: [
      "revision",
      "backup",
      "restore",
      "quota",
      "lock",
      "storage",
      "14 days",
    ],
    steps: [
      [
        "Save configuration changes",
        "Use Save draft after editing variables, layouts or profiles. Personalizing the proof form is a separate operation; Save proof records that reviewed set of values.",
      ],
      [
        "Review history",
        "Open Draft history to inspect prior revisions, lock a backup or restore a revision as the current draft.",
      ],
      [
        "Publish a version",
        "Publish creates an immutable version for integrations that need a stable definition. It protects the corresponding source backup for 14 days.",
      ],
      [
        "Export a backup",
        "Open Project settings → Download workspace backup. The JSON backup contains browser workspace records; retain stored server asset/font files separately when moving a deployment.",
      ],
    ],
    notes: [
      "Browser records belong to this browser and origin. API memory mode loses its projects/jobs on restart; file-backed stores provide server persistence when configured. A workspace JSON backup alone does not copy server asset or font bytes.",
    ],
    related: ["defaults", "live-proof", "render-jobs"],
    actions: ["history", "settings"],
  },
  {
    id: "variables",
    topic: "fields",
    title: "Configure variables and generated forms",
    summary:
      "Choose controls, sources, defaults, validation and form organization in one place.",
    keywords: [
      "field",
      "token",
      "required",
      "group",
      "tab",
      "price",
      "QR",
      "calendar",
      "form layout",
      "column",
      "section",
      "filters",
    ],
    steps: [
      [
        "Create a variable",
        "Open Fields & variables, select the main project, add a field, and set its Display label, Variable token and Control type.",
      ],
      [
        "Set its behavior",
        "Configure a default or source, required/read-only/system flags, helper text, character or numeric bounds, validation expression, formatting and any price upcharge. Read-only and system controls stay visible with their resolved values, a disabled background and readable text. They cannot be edited; part-specific permissions still apply.",
      ],
      [
        "Find a field",
        "Search the field list or use the System, Calculated and Required filters above it. Each filter is off by default; turning one on shows only matching fields.",
      ],
      [
        "Organize the form",
        "Open Form layout. A new layout starts with one column; Add column, Add section and Add tab sit at the right of the toolbar. Not in a section and Removed from form are dropdowns: choose a field to place it. On a tab, the Sections dropdown lists only sections not already in that tab. Select a field shows the fields you can add to a section, and + Add a field creates one in place. Set the column width and horizontal or vertical alignment under the presentation controls.",
      ],
      [
        "Search long lists",
        "Lists with five or more options open a searchable dropdown: type to filter and scroll to load more. Shorter lists stay as a plain selector.",
      ],
      [
        "Hide or remove a form control",
        "In Form layout, Hide keeps the control in the editor with a Show action. Remove moves it to Available fields, where Add to form restores its section and settings. Unassign moves a control out of its section. When sections exist, only assigned controls appear in the form; assign an ungrouped variable to a section to include it. Without sections, all available variables are shown. These form actions retain the variable, its value and its document bindings. The separate Hidden variable flag and conditional rules can also suppress artwork.",
      ],
      [
        "Try the form",
        "Open Live proof and exercise the controls. Correct validation and visibility behavior before publishing or building a bulk template.",
      ],
    ],
    options: [
      [
        "Available controls",
        "Text, textarea, rich text, checkbox, dropdown, combobox, radio, image, image list, date, date/time, time, phone, email, QR, address, map, preset, number, calendar and color.",
      ],
      [
        "Variable token",
        "A stable key identifies the variable in templates and adapters. Display labels can change without replacing the field identity.",
      ],
      [
        "Generated values",
        "System, read-only and auto-generated values follow their configuration and trusted sources rather than arbitrary submitted input.",
      ],
      [
        "Static values and options",
        "Set Form display to Show on main form to display read-only variables, system values and fixed choice selections. Turn Editable off to keep a value fixed; hiding or removing a form control alone does not protect submitted values. Part-specific Form display changes save only that setting; Inherit restores the main project's choice. Reusable form inputs also have separate Editable, Hidden on form and Remove input controls.",
      ],
    ],
    related: ["part-overrides", "rules", "mapping", "field-policies"],
    actions: ["fields", "proof"],
  },
  {
    id: "calculations",
    topic: "fields",
    title: "Calculate values from variables",
    summary:
      "Build names, conditional results and text formatting that update with the form.",
    keywords: [
      "calculation",
      "formula",
      "join",
      "replace",
      "derived",
      "read only",
    ],
    steps: [
      [
        "Enable a calculated value",
        "Select a field in Fields & variables, expand Calculated value and choose Calculate from variables and rules.",
      ],
      [
        "Build the result",
        "Choose a fixed value, another variable, joined text, a conditional value or literal replacements. Conditions can match all or any comparisons. Add and reorder text parts as needed.",
      ],
      [
        "Check the form and export",
        "Change the source fields in Live proof. The calculated result updates automatically and stays read only. Generate a document to check the final artwork.",
      ],
    ],
    options: [
      [
        "Names and formatting",
        "Join a first and last name, add a middle initial only when present, or append a comma and accolade conditionally. Literal replacements can convert formatting markers to line breaks.",
      ],
      [
        "Part inheritance",
        "A part stores only its changed calculation. Choose Use the configured default and input to disable it explicitly, or reset Calculation inheritance to follow the main project again.",
      ],
      [
        "Saved and bulk inputs",
        "Calculated outputs are excluded from editable saved-profile inputs and bulk-template columns. Supply their source inputs instead.",
      ],
    ],
    notes: [
      "Calculations are bounded data operations, not scripts. Unknown references and dependency cycles are rejected. Oversized results produce a validation error without crashing the proof.",
    ],
    related: ["variables", "part-overrides", "rules"],
    actions: ["fields", "proof"],
  },
  {
    id: "part-overrides",
    topic: "fields",
    title: "Use sparse part overrides and inheritance",
    summary:
      "Customize a part while keeping every unconfigured property linked to the main project.",
    keywords: [
      "fallback",
      "inherit",
      "override",
      "reset",
      "system values",
      "SKU",
    ],
    steps: [
      [
        "Configure the main project first",
        "Set shared field labels, defaults, sources, controls and validation in Fields & variables.",
      ],
      [
        "Choose an Editing scope",
        "Select a part and change only the properties that differ. A part may replace a default, label, source or another supported property.",
      ],
      [
        "Restore inheritance",
        "Reset an overridden property, or use Remove all overrides for this variable. The saved delta is removed and the main-project value takes effect again.",
      ],
      [
        "Compare the results",
        "Switch Project / part in Live proof or select Part in Bulk generation to review the effective configuration.",
      ],
    ],
    notes: [
      "Only explicit overrides are persisted. Inherited variable definitions are computed at runtime and are never saved as complete part copies. Explicit part system values are separate protected values.",
    ],
    related: ["variables", "mapping", "bulk-template"],
    actions: ["fields"],
  },
  {
    id: "rules",
    topic: "fields",
    title: "Build conditions, rules and presets",
    summary:
      "Show the right controls, apply predefined values, and keep dependent fields consistent.",
    keywords: [
      "visibility",
      "trigger",
      "condition",
      "validation",
      "format",
      "preset",
      "image rule",
    ],
    steps: [
      [
        "Set field conditions",
        "In Fields & variables, configure the source field, operator, comparison value and show/hide action for conditional fields.",
      ],
      [
        "Create document rules",
        "Use Rules & presets for conditional actions and reusable value selections. Review dependency and target validation when assigning values.",
      ],
      [
        "Save reusable definitions",
        "Use Configuration → Mapping to create and version rules, presets, validation or formatting resources, then apply a saved version to a document.",
      ],
      [
        "Test every branch",
        "Change the controlling values in Live proof and check visibility, required fields, image choices and final exported content.",
      ],
    ],
    notes: [
      "Hidden controls do not make hidden data authoritative. Row validation and protected-source rules still apply during generation. Image-option mappings use stored choice values rather than display labels.",
    ],
    related: ["prepopulation", "datasets", "forms"],
    actions: ["fields", "mapping"],
  },
  {
    id: "prepopulation",
    topic: "fields",
    title: "Create prepopulation records",
    summary:
      "Fill a related set of fields from one selection while preserving protected values and part inheritance.",
    keywords: ["record", "preset records", "XML", "prepopulate", "assignment"],
    steps: [
      [
        "Choose a preset control",
        "In Fields & variables, select a preset field and use Preset records to configure options and their target variables.",
      ],
      [
        "Define record values",
        "Enter record labels, stable selection values and target assignments. Review validation when importing supported legacy record XML.",
      ],
      [
        "Configure part differences",
        "Store only the part records or properties that need to differ; leave the rest inherited.",
      ],
      [
        "Review the result",
        "Select records in the generated form and inspect dependent controls and the exported document.",
      ],
    ],
    notes: [
      "Record assignments respect protected fields and dependency rules. A prepopulation record is not a direct database write.",
    ],
    related: ["rules", "part-overrides", "datasets"],
    actions: ["fields"],
  },
  {
    id: "forms",
    topic: "fields",
    title: "Create reusable forms and saved form profiles",
    summary:
      "Store form definitions in Configuration and reuse entered value profiles while proofing.",
    keywords: ["library", "form instance", "repeated", "profile", "version"],
    steps: [
      [
        "Build a reusable definition",
        "Open Configuration → Forms. Shared library is selected by default for forms reusable across projects. Choose Configuration scope → This project for a private form. Assignments and visibility settings remain specific to each project. Choose Create reusable form or Generate form from document, review inputs, and Save library version. Suggestions omit protected and dependent inputs and clear current defaults and legacy data.",
      ],
      [
        "Save and apply a version",
        "Choose Apply version to review suggested mappings. Matching uses variable keys, normalized keys and compatible labels; ambiguous matches remain unmapped. Select a target or Expose an input without artwork to collect an extra value. Assign form and save the draft. Only mapped inputs appear by default. Fields & variables → Reusable forms lets you revise mappings and expose more inputs later.",
      ],
      [
        "Map a global profile collection",
        "In the form library editor, Global profile collection uses this form’s own reusable profiles by default. Select another saved form to suggest profile-to-form mappings, review the input mappings, and save a new version. Live proof maps profile values through the form into each project variable. Linked source collections are read-only through that form. Update values through the original form; Your profiles supports metadata and sharing management. Private account and sharing permissions still apply.",
      ],
      [
        "Assign global profile collections to projects",
        "Open Configuration → Global Profiles to define predefined datasets. Manage predefined dataset creates named records with values, active state, user/group/company links and optional role restrictions. Multiple datasets can map to the same project variables. Live proof offers only authorized records and Apply predefined profile fills mapped, writable variables. Audience links are alternatives; selected roles additionally restrict access. Empty audience and role lists grant no access. Memberships come from the authenticated host. Definitions and assignments pin versions; values and grants remain in private storage. Personal saved profile values and Your profiles retain separate account-owned records.",
      ],
      [
        "Choose global profile records",
        "Profile selectors appear only where global profiles are configured. When the whole form uses one distinct profile, a single selector at the top fills every applicable field. When the form uses several profiles, each section with profile-linked fields shows its own selector limited to that section's profile, and sections that use the same profile can pick different records. Selections are saved with the proof and restored on reload. Variables linked to different profiles cannot share a section: Fields & variables lists each conflict and Save and Publish stay blocked until it is resolved. Unmapped variables can sit alongside mapped ones, and Generate Form keeps profiles in separate sections.",
      ],
      [
        "Use form profiles",
        "In Live proof, expand Reusable form profiles for configured instances. Enter, save or choose a value profile, then apply it.",
      ],
      [
        "Manage personal profiles in a connected workspace",
        "Select a published document version. Save new profile creates an account-owned record; Update selected profile checks its loaded revision, and Delete selected profile removes it. For a form applied from the library, Profile collection offers This document or Reusable form across documents. The latter reuses your profile in other documents with the same verified library form. Each instance applies its own mapping. A changed form version requires Review with current form; local edits that differ from the retained library definition use document profiles. Reload profiles lets you review a conflict without losing edits.",
      ],
      [
        "Share a saved value profile",
        "In a connected workspace, select one of your profiles, expand Profile sharing, enter recipient account IDs one per line, then Save sharing. Recipients find it under Shared with you and can apply it through their own instance mapping or save a personal copy. They cannot update or delete the owner's record. Remove all recipients to revoke future reads. Already delivered values, copies and accepted jobs remain. Sharing is disabled for the single-account demo and operator support contexts; the documented two-account local demonstration needs no database.",
      ],
      [
        "Manage profiles without a source document",
        "Open Your profiles in the workspace sidebar to browse your saved document and reusable-form collections. Select a profile to rename it, remove existing recipients, stop sharing, download its typed data or delete it. These controls work even when every source document has been removed. Metadata pages do not include field values. Refresh and select the profile again after a revision conflict; local name edits are retained. Use Live proof with a verified form to edit field values or add recipients.",
      ],
    ],
    notes: [
      "A library form version defines controls. A saved form profile stores a set of entered values. Applying a library version copies its snapshot; later versions do not silently change existing documents.",
      "Connected personal profiles use server storage; the default file and PostgreSQL providers survive API restarts. A memory provider is explicitly labeled. Protected and host-derived values are recomputed, and applying a profile respects the selected part's read-only, hidden, lookup and system fields. Library-backed demo forms use the API's shared demo account without a database; older non-library demo forms keep their browser-local profiles. Switching collections starts a new editor draft.",
    ],
    related: ["rules", "save-versions", "datasets"],
    actions: ["forms", "globalProfiles", "proof", "profiles"],
  },
  {
    id: "assets",
    topic: "configuration",
    title: "Store assets and personalize images",
    summary:
      "Reuse managed images and control the editing tools available in generated forms.",
    keywords: [
      "upload",
      "crop",
      "rotate",
      "background removal",
      "DPI",
      "image list",
      "thumbnail gallery",
      "ImageBankListView",
      "DAM",
    ],
    steps: [
      [
        "Add assets",
        "Open Configuration → Assets for this project's attached and referenced images. Uploads attach here; choose Asset library scope → Shared library to add other managed images explicitly. Search or page through the thumbnails, download verified managed images, and remove unused library attachments without deleting shared bytes. Images used by the document remain attached. Save draft to retain attachments. The same asset inventory is available to JavaScript hosts through proof-asset-library or AssetLibrary. Image variables also support uploads and a searchable thumbnail picker. Edit image uses only the variable's allowed tools.",
      ],
      [
        "Set image permissions",
        "In Fields & variables, configure image upload limits, minimum DPI, aspect constraints and the allowed editing tools.",
      ],
      [
        "Review adjustments",
        "Upload or replace an image to open Edit image. Only the variable's allowed tools appear. Crop by dragging a selection on the original image; move it inside the selection or resize with edge/corner handles. Drag outside the selection to start a new one. Arrow keys and Shift support precise keyboard adjustments. Preview draft changes, then choose Save image; Cancel or Escape keeps the previous selection. When background removal is enabled, Remove background detects the subject on your device and previews a transparent cutout. The first use loads the bundled model; progress and Cancel background removal remain available. Save image keeps the result. Edit image reopens the original with the saved adjustments and cutout. Reset edits starts from the original upload, and resets inside the modal apply only when saved. Fixed crop aspect exposes a width/height ratio in the variable settings. Check the proof and image-resolution diagnostics. System, read-only and protected-part images offer View image with disabled controls.",
      ],
      [
        "Use an image library",
        "For an image or ImageList variable, set Source to dam and configure Library provider and Library root folder. Live proof offers folder browsing, search and revision-specific selection. A part can override just its source. Try the Brand library card starter without a database or remote catalog.",
      ],
      [
        "Offer image choices",
        "For a configured image-list, assign managed images to its options and choose Image choice display → Thumbnail gallery. Customers select with thumbnails or arrow keys. Larger lists offer search and pages of twelve options. A part can override just the display; Inherit restores the main project. Reusable form inputs support the same gallery setting and image assignments.",
      ],
      [
        "Reuse selections in bulk",
        "Download image selection saves signed JSON for the same document, variable and part. Paste that JSON into the image column of a form-matched bulk template. Switching parts requires a fresh selection for that part's folder.",
      ],
    ],
    notes: [
      "Remove background runs on the proof server for PNG, JPEG and WebP originals up to 20 MB and 24 megapixels. Choose Solid or white background for logos and product shots: it clears the colour touching the image edge, the Tolerance slider widens or narrows the match, and clicking the cutout clears enclosed areas such as the inside of a letter. Choose Photo subject for people or busy backgrounds. Nothing is downloaded to your browser; the transparent result is stored through the workspace's image service. The separate Remove near-white background checkbox retains the color-key option. Only fields allowing the removeBackground tool offer these controls, and an upload service is required to retain a cutout. Remote DAM catalogs use host-supplied providers or the configured server HTTP adapter; the included library is an offline demonstration.",
    ],
    related: ["base-shape", "output-profiles", "embedding"],
    actions: ["assets", "fields"],
  },
  {
    id: "managed-fonts",
    topic: "configuration",
    title: "Store and choose managed fonts",
    summary:
      "Use stored font bytes for consistent previews and exports across computers.",
    keywords: [
      "TTF",
      "OTF",
      "WOFF",
      "WOFF2",
      "installed fonts",
      "baseline",
      "substitution",
      "font family",
    ],
    steps: [
      [
        "Browse the font library",
        "Open Configuration → Fonts for this project's managed font manifest. Choose Font library scope → Shared library to browse the installed baseline and other reusable managed faces. Create text style attaches matching faces to this project. Project font uploads attach here too; Save draft retains the manifest. Search and preview managed bytes independently of viewer-installed fonts.",
      ],
      [
        "Add a font if needed",
        "Upload a supported static TTF, OTF, WOFF or WOFF2 face. Font validation and embedding permissions determine whether it is accepted.",
      ],
      [
        "Pin the face",
        "Select a text layer in Document designer and choose Managed font face, or save a reusable text style with that face.",
      ],
      [
        "Check imported typography",
        "Review substitutions after document import. Matching active managed faces can be pinned to imported text; unavailable fonts need replacement and visual review.",
      ],
    ],
    notes: [
      "The installed baseline was copied into managed storage using the installed-font tools. Rendering uses stored bytes, not whatever fonts happen to be installed on its host. Bitmap, restricted or unsupported faces may be skipped; newly installed system fonts are not automatically added to the catalog.",
      "Download face verifies the stored bytes before delivery. Archive removes a tenant-uploaded face from active choices while retaining existing document snapshots. Hosts can embed the same FontLibrary React panel or proof-font-library JavaScript element with their own catalog, byte, upload and archive callbacks; an API is optional.",
    ],
    related: ["typography", "import-document", "defaults"],
    actions: ["fonts", "designer"],
  },
  {
    id: "datasets",
    topic: "configuration",
    title: "Create lists, tables and uploaded datasets",
    summary:
      "Build versioned choice lists or typed tables, including reviewed spreadsheet uploads.",
    keywords: [
      "CSV",
      "TSV",
      "XLSX",
      "worksheet",
      "primary key",
      "table",
      "list",
      "publish",
      "multiple datasets",
    ],
    steps: [
      [
        "Choose a dataset",
        "Open Configuration → Datasets. The reusable dataset library defaults to Shared library for definitions configurable across projects. Choose Configuration scope → This project for private definitions. Document datasets adds independent datasets directly to the document. Each collection shows its dataset count.",
      ],
      [
        "Enter or import rows",
        "Edit values directly or upload CSV, TSV or XLSX. Inspect worksheets, headers, detected types and the preview before accepting the import.",
      ],
      [
        "Publish a library version",
        "Save library version, then use Add another dataset for a separate entry. Apply version for every dataset the document needs; they accumulate without replacing each other. In Document datasets, use Add choice dataset or Add table dataset, edit the data and Publish dataset version. Save the document draft to retain the collection and bindings.",
      ],
      [
        "Upgrade deliberately",
        "Switch between datasets using Edit dataset; unfinished edits stay separate while this editor remains open. Publish each dataset before leaving the page. New versions do not move existing field bindings; select upgrades explicitly in Mapping.",
      ],
    ],
    notes: [
      "Table primary keys must be present and unique. Stored choice values and display labels can differ. Workbook formulas are not executed; only supported cached values are read.",
    ],
    related: ["mapping", "bulk-import", "forms"],
    actions: ["datasets", "mapping"],
  },
  {
    id: "mapping",
    topic: "configuration",
    title: "Map fields to datasets and profile values",
    summary:
      "Connect variable sources to a selected table row, profile, order or other supported context.",
    keywords: [
      "lookup",
      "row",
      "source",
      "user",
      "order",
      "dynamic",
      "guest",
      "read only",
    ],
    steps: [
      [
        "Apply the data first",
        "Add a saved dataset through Configuration → Datasets when the mapping needs local table or list data.",
      ],
      [
        "Choose a variable",
        "Open Configuration → Mapping and select Mapping variable. Set its source and, for a dataset selector, the value and label columns.",
      ],
      [
        "Add row lookups",
        "Bind dependent variables to a selector, match column and result column. Lookup fields become read-only in the generated form.",
      ],
      [
        "Test profile fallback",
        "Use the Profile-driven business card starter to switch Alex, Jamie and Guest. Missing profile keys retain configured defaults; part-specific sources can differ.",
      ],
      [
        "Load host-owned options",
        "For a dynamic source, choose Dynamic response → Choice catalog. The host supplies option values, labels, managed-image IDs and an optional default. Try Dynamic catalogue card and switch Alex, Jamie and Guest in Live proof. Review changed options before generating again.",
      ],
      [
        "Use images from a connected directory",
        "Your integration can import directory images into managed storage before loading its options. Use an image-list variable with Dynamic response → Choice catalog and choose thumbnail display when appropriate. Select an option and review the proof; accepted documents retain the imported pixels even if the directory later changes. A failed source/image refresh blocks new generation until it succeeds.",
      ],
      [
        "Collect source parameters",
        "Expand Dynamic source parameter forms, add a named form and host binding key, and configure its public controls. Save it, select Parameter form on the dynamic mapping, then save the draft. In Live proof, Source parameters opens the modal; Load source data applies values and Refresh source data bypasses the host cache. The Regional catalogue card demonstrates North and South without a database.",
      ],
      [
        "Allow an optional source to be skipped",
        "Enable Allow this source to be skipped on its parameter form. In Fields & variables → Part overrides, Source requirement can inherit the project, require loading or permit skip. Only explicit part requirements are saved. The Optional directory card shows configured values when skipped; its Partner part requires the source. Skip does not bypass form validation or automatically occur after a lookup failure.",
      ],
      [
        "Normalize parameter whitespace",
        "Choose Preserve or Trim for each parameter before saving its form. Trim removes surrounding whitespace before validation and context hashing. Native fields preserve it by default; controls generated from supported legacy dynamic metadata use Trim.",
      ],
    ],
    notes: [
      "Mapping edits the main-project source configuration. Part source overrides are configured in Fields & variables. Live directory/order data can use an injected provider or the server's configured HTTP context service. A configured HTTP identity bridge can supply authenticated principals and queued-owner checks; the host still provides sign-in and directory authorization. The local demo supplies server-owned fixtures when no service is configured.",
      "Choice catalogs validate submitted selections and remain fixed in accepted jobs. Canonical public parameters are included in the context hash, saved proofs and retained job/batch snapshots. The legacy provider checks host authorization and system parameters even on cache hits and supports bounded expiry and forced refresh. URLs, credentials, legacy identity mappings and live ESM SQL writes remain host configuration/integration work.",
    ],
    related: ["datasets", "part-overrides", "impersonation"],
    actions: ["mapping", "fields"],
  },
  {
    id: "structured-configuration",
    topic: "configuration",
    title: "Edit configuration with forms or JSON",
    summary:
      "Edit advanced settings using typed controls, lists and objects without writing JSON.",
    keywords: [
      "JSON",
      "GUI",
      "configuration",
      "trigger",
      "form tab",
      "metadata",
      "JavaScript",
    ],
    steps: [
      [
        "Open the form editor",
        "Configuration inputs open in Form editor. Expand nested values, edit text and numbers, or toggle booleans. Triggers, form tabs and reusable forms provide templates with variable and group selectors.",
      ],
      [
        "Build objects and lists",
        "Add named properties or list items, change a value type when available, and use Move up or Move down to reorder items. Collections show 30 entries at a time. Only explicit part overrides are persisted; removing an override restores project inheritance.",
      ],
      [
        "Review and apply",
        "Choose JSON editor to inspect or edit the same draft. Invalid JSON stays visible until corrected. Apply configuration validates through the receiving editor. Operational changes invalidate prepared reviews, and credentials use their separate controls.",
      ],
      [
        "Configure from JavaScript",
        "Use ConfigurationEditor from the authoring or SDK module, or set value and options on proof-configuration. Supply field hints, fresh-item factories and an asynchronous onSave validator. The control needs no project or database. proof-authoring also supports mode=summary with host navigation/export events.",
      ],
    ],
    notes: [
      "Specialized dataset, preset and form editors remain available. File uploads are import actions; edit their resulting data in the corresponding GUI.",
    ],
    related: ["defaults", "datasets", "part-overrides"],
    actions: ["fields", "integrations"],
  },
  {
    id: "defaults",
    topic: "configuration",
    title: "Version resources and set workspace defaults",
    summary:
      "Reuse exact form, style, dataset, page and output settings without changing older documents.",
    keywords: [
      "catalog",
      "archive",
      "JSON",
      "page size",
      "quota",
      "retention",
      "default",
    ],
    steps: [
      [
        "Find the resource category",
        "Inside a project, Project configuration holds Assets, Forms, Profiles, Datasets, Mapping and production-profile assignments. Configuration holds shared catalogs, including Fonts, Global Profiles, Page bleed profiles, Sheets & paper and Output profiles for the whole workspace. Each page lists saved entries first; choose Create to open a step-by-step setup (Name it, Set it up, Save), with JSON available for exact configuration. On narrow screens, open the sidebar icon, then choose a destination. All building blocks shows every entry, including text styles and page sizes.",
      ],
      [
        "Save a named version",
        "Forms, Global Profiles, output profiles, rules and datasets are reusable across projects. Forms, rules and dataset libraries open in Configuration. The Shared library scope option identifies cross-project entries. Applied visibility settings and variable mappings stay with the selected project. This project shows private definitions. Save shared copy or Copy to this project creates an independent version history. Search, archive, export and import definitions in the chosen scope.",
      ],
      [
        "Choose exact defaults",
        "Choose Shared library to set tenant defaults from shared versions for new blank documents. Project-only entries cannot become tenant defaults. Review the unlocked backup quota and protected draft-history rules.",
      ],
    ],
    notes: [
      "Applying or defaulting a resource copies its snapshot into a document. Publishing a new catalog version does not update existing documents. Output profiles and Global Profiles are global definitions assigned to projects. Shared rules resolve variable keys when applied and create project-specific visibility settings.",
    ],
    related: ["workspace-setup", "save-versions", "forms"],
    actions: ["configuration", "setup"],
  },
  {
    id: "layers",
    topic: "design",
    title: "Lay out pages, layers and variables",
    summary:
      "Position content precisely, bind it to fields, and control how artwork fits its frame.",
    keywords: [
      "drag",
      "resize",
      "lock",
      "hide",
      "anchor",
      "overflow",
      "keyboard",
      "undo",
      "redo",
    ],
    steps: [
      [
        "Build the page",
        "Open Document designer. Select a page or add another, then add text, rich text, images, shapes, rules, calendars or QR layers.",
      ],
      [
        "Connect variables",
        "Select a layer, choose its variable binding, and use the inspector to set position, width, height, rotation, alignment and content fitting.",
      ],
      [
        "Control the layout",
        "Lock or hide layers, adjust their order, and configure anchors, stretching, clipping, shrinking or flow placement where available.",
      ],
      [
        "Position image artwork precisely",
        "Select an image layer and open Image placement. Stage independent X/Y scales and offsets in points, then Apply image placement. Reset image placement draft discards unsaved edits; Remove image placement restores normal fitting. Clip image to frame is independent. Precise placement is shared by preview and export, retained in native packages, and included in effective DPI checks. Unclipped artwork contributes to content-sized page bounds.",
      ],
      [
        "Edit vector source coordinates",
        "Add Vector creates an editable path. Open Vector path & viewport to stage SVG commands and source dimensions, or enable a custom viewport with negative origins and upward PDF-style or downward SVG-style Y. Apply vector path commits the valid draft; Reset vector draft discards edits. The layer frame controls placement and final size. Viewport geometry is shared by preview, output and document-shape cropping, and stays in native packages.",
      ],
      [
        "Replace PDF image artwork",
        "Select an image layer and open PDF artwork. Choose a PDF and page number, then Prepare PDF artwork. Nothing changes until Apply PDF artwork. The image frame and variable binding stay in place; its default changes to the replacement and existing part overrides remain. A local image-list default updates only that option's artwork. Keep current image placement retains its scale and offsets; otherwise fitting resets. PDF output profiles preserve the retained vector page, while preview and raster output use managed pixels. Cancel or discard a prepared import to keep the current artwork. Use preview pixels only removes the retained PDF source without deleting the image.",
      ],
      [
        "Review and save",
        "Use undo/redo to compare changes, inspect all pages in Live proof, and save the document draft.",
      ],
    ],
    options: [
      [
        "Keyboard in the designer",
        "Use the undo/redo buttons to review changes. Ctrl/Cmd+Z undoes, and Ctrl/Cmd+D duplicates. With an unlocked fixed layer selected, arrows move it 1 point; Shift+arrows move 10 points. Delete/Backspace removes a layer. Shortcuts do not replace typing inside inputs.",
      ],
    ],
    related: ["base-shape", "typography", "flow"],
    actions: ["designer"],
  },
  {
    id: "base-shape",
    topic: "design",
    title: "Import a base and shape the document",
    summary:
      "Use an alpha silhouette or a vector frame to crop every layer to the document shape.",
    keywords: [
      "apple",
      "PNG",
      "silhouette",
      "shape",
      "frame",
      "crop",
      "base project",
      "ellipse",
      "rounded",
    ],
    steps: [
      [
        "Import a base project",
        "In Document designer, choose Import base project. Use canonical project JSON, PDF, IDML, INDD/INDT, a publishing-project ZIP or a supported image to replace this draft's configuration and pages. Review publishing-package conversion warnings before accepting the draft.",
      ],
      [
        "Use an image silhouette",
        "A transparent PNG shaped like an apple gives the document an apple silhouette. Transparent margins are trimmed; the base image layer starts locked.",
      ],
      [
        "Choose another frame",
        "Open Page settings and select Document frame: rectangle, ellipse/circle or rounded rectangle. Upload base document accepts PNG, JPEG, WebP or TIFF artwork and adopts its visible size and alpha silhouette, while keeping current artwork and variables. The image becomes a locked base layer; another upload replaces it. Undo restores the previous page. Opaque images use rectangular bounds.",
      ],
      [
        "Crop to an existing shape",
        "Use Crop document to shape with an unrotated rectangle, ellipse or path layer. The document adopts that shape's bounds and crops artwork to it.",
      ],
    ],
    notes: [
      "Base import preserves the current document identity and can be undone. Save the draft to retain it. Alpha tracing approximates the boundary; holes and separate regions are retained. An opaque image stays rectangular, and a fully transparent image is rejected.",
      "PDF media remains rectangular with unpainted space outside the shape. PNG/SVG preserve transparency; JPEG and CMYK raster outputs flatten it to white.",
    ],
    related: ["import-document", "transparent-output", "layers"],
    actions: ["designer"],
  },
  {
    id: "typography",
    topic: "design",
    title: "Use shared typography and layer overrides",
    summary:
      "Keep document defaults, named text styles and individual layer changes working together.",
    keywords: [
      "styles",
      "font",
      "paper",
      "role",
      "inherit",
      "custom styling",
      "bands",
    ],
    steps: [
      [
        "Set the document style",
        "Open Styles in Document designer to edit paper, fallback typography, the five built-in text roles and custom styles.",
      ],
      [
        "Link a layer",
        "Select a text layer and choose Text style. Choose a managed font face when consistent stored typography is required.",
      ],
      [
        "Override only what differs",
        "Change a layer property such as font size or alignment. Other properties continue inheriting. Reset a property to resume inheritance, or choose Custom styling to keep direct values.",
      ],
    ],
    options: [
      [
        "Page paper",
        "New pages can inherit the document paper. A page can explicitly choose its own background or transparency.",
      ],
      [
        "Reusable styles",
        "Save style resources with their pinned font snapshots for reuse in other documents.",
      ],
    ],
    related: ["managed-fonts", "flow", "defaults"],
    actions: ["designer", "fonts"],
  },
  {
    id: "flow",
    topic: "design",
    title: "Flow stories through columns and pages",
    summary:
      "Let rich content continue through linked regions instead of clipping to one text box.",
    keywords: [
      "newsletter",
      "overflow",
      "continuation",
      "region",
      "growth",
      "push",
      "header",
      "footer",
    ],
    steps: [
      [
        "Define the regions",
        "Open Page settings → Flow regions. Add columns and link them in list order or set Continue into explicitly.",
      ],
      [
        "Assign flow content",
        "Select a text or rich-text layer, choose Flow with content, and select its region and flow order.",
      ],
      [
        "Set placement limits",
        "Configure growth, spacing, minimum/maximum height, push behavior and continue overflow to allow additional regions or pages. With shrink overflow, Add copyfit limits sets minimum font/line/paragraph-spacing percentages, an optional maximum lines per paragraph and a uniform group shared across frames. Apply saves valid edits; remaining overflow is reported at the limits. Hidden frames do not participate in shared reductions.",
      ],
      [
        "Inspect pagination",
        "Use the Flowing newsletter starter. Change the form content, review continuation pages and any warnings, and check headers/footers and the exported document.",
      ],
    ],
    related: ["layers", "typography", "output-profiles"],
    actions: ["designer", "proof"],
  },
  {
    id: "layout-groups",
    topic: "design",
    title: "Reflow layers in nested layout groups",
    summary:
      "Stack visible content, retain relative overlays and center optional address rows using shared proof/export measurements.",
    keywords: [
      "group",
      "vertical",
      "horizontal",
      "overlay",
      "margins",
      "leading",
      "font box",
      "nested",
    ],
    steps: [
      [
        "Configure containers",
        "Open Document designer → Page → Layout groups. Create or select a group, choose Vertical, Horizontal or Overlay, and edit its parent, size, alignment, gap and anchors. Blank width/height fits visible content. Group size limits adds optional minimum/maximum width and height in points. Apply group limits validates the draft; Reset group limit draft restores saved values, and Remove group limits restores unrestricted sizing. Limits apply before anchoring and parent placement, while child sizes stay authored. Enable Clip contents to group to crop descendants to those bounded dimensions; nested clips intersect. Empty groups collapse.",
      ],
      [
        "Assign layers",
        "Select a layer → Placement & overflow → Layout group. Order and margins position stacked layers; overlay coordinates are relative to the group. Grouped layers use fixed placement without page anchors or stretching.",
      ],
      [
        "Measure text",
        "Height fits text content measures the text before placing the following block. Font ascent and descent removes trailing leading from the frame height and uses an ascent baseline. Imported paragraph leading remains attached to its text runs.",
      ],
      [
        "Edit and inspect",
        "Clicking selects a layer without changing its group or position. Arrow keys adjust stacked layer margins. Deliberately dragging a layer or choosing Page positioning detaches it into page positioning and lets its former group reflow; resizing keeps the group and fixes its height. Undo restores the original placement. Save draft, then change form values in Live proof to verify reflow. Groups stay on one logical page; use Flow regions for continuation pages.",
      ],
    ],
    related: ["layers", "flow", "import-document"],
    actions: ["designer", "proof"],
  },
  {
    id: "live-proof",
    topic: "proof",
    title: "Personalize, review and save a live proof",
    summary:
      "Use the generated form and the same scene geometry used by document export.",
    keywords: [
      "form",
      "preview",
      "approval",
      "initials",
      "comments",
      "saved proofs",
      "reset",
      "zoom",
    ],
    steps: [
      [
        "Select the context",
        "Open Live proof, choose Project / part and, when present, a Demo profile. Wait for profile values to load before generating.",
      ],
      [
        "Personalize and inspect",
        "Enter values and review the artwork as it updates. Use previous/next page, zoom and Fullscreen proof to inspect the result.",
      ],
      [
        "Record the review",
        "Add proof comments if enabled. To save an approved proof, check approval and enter your initials. Save proof stores the reviewed values in this browser.",
      ],
      [
        "Generate the artifact",
        "Select an Output profile and choose Generate document. Each completed generation offers its allowed document download plus Variables (JSON) and Variables (XVP). These contain the variable values used for that specific render, including calculated and profile values, and remain unchanged when the form is edited. Different profiles and data can be queued while another request runs.",
      ],
    ],
    options: [
      [
        "Reset to defaults",
        "Clears entered values so effective document and part defaults apply again.",
      ],
      [
        "Saved proofs",
        "Restore entered values and review details. Restoring a proof does not replace the currently selected profile identity.",
      ],
    ],
    notes: [
      "Changing form data or output keeps earlier artifacts labeled in the queue. If the same output profile and data already have an active generation, choose Keep existing generation or Cancel existing and start new. Completed identical requests reuse their artifact. Queue cards remain for this open proofing session; the Render operations screen retains durable server history. Refresh failed profile values before generating again.",
      "The default demo saves proofs in the shared browser workspace; configured tenant mode uses API-owned records. Authenticated hosts can also use ProofClient.saveProof, listProofs and getProof for server-owned records: users see their own proofs, and Tenant Administrators can review all proofs in their tenant. Older server records without an owner remain available to administrators.",
    ],
    related: ["impersonation", "output-profiles", "troubleshooting"],
    actions: ["proof"],
  },
  {
    id: "impersonation",
    topic: "proof",
    title: "Try impersonation in a separate tab",
    summary:
      "Open a modal form in a new tab with independent personalization values.",
    keywords: [
      "impersonation",
      "new tab",
      "modal",
      "Alex",
      "Jamie",
      "guest",
      "profile",
    ],
    steps: [
      [
        "Open the new tab",
        "Choose Impersonation immediately below Live proof, or Test this form in Live proof or Fields & variables. If real users are available, choose a user or Nobody, then Open proofing page. With no users, the demo opens directly as Nobody. Demo fixture profiles are not users. The demo copies the current unsaved draft, values and part; configured workspaces open the dialog at any time, starting from Live proof's Document version and Form from choice (or the project default), which you can change before opening.",
      ],
      [
        "Personalize in the modal",
        "The generated form opens automatically in a centered modal, using the same sections and up to three columns as View Form. Only assigned controls appear when sections exist; other variables keep their document values. Narrow forms stack their sections. Change the part and enter values; the document updates as it does in Live proof. The customer preview has no demo profile selector.",
      ],
      [
        "Inspect and export",
        "Choose View proof, press Escape or close the modal to inspect the full proof. Edit form reopens it. Review, save a proof or generate the document using the same controls.",
      ],
    ],
    notes: [
      "Each launch has independent working values. Demo values survive reload in that tab and saved proofs use browser storage. Configured previews keep their credential and working values only in memory; Save proof writes a server-owned record for your current account and selected publication.",
      "A configured preview is bound to the source workspace and opened tab. Disconnect, revoked or changed access, closing the source, or a lost connection heartbeat ends it. Reloaded, expired or copied links must be reopened from Live proof. Allow popups if the browser blocks the new tab; at most eight previews can be open per connection.",
      "The picker lists active known real memberships. Document and user managers can preview those users; other proof users can choose only themselves or Nobody. The host supplies the selected user's profile data. Nobody uses document defaults without loading a private user or order profile. API authorization and saved-record ownership remain your checked account. Platform operators use a separate audited Tenant support access context, whose banner remains visible.",
    ],
    related: ["mapping", "live-proof", "access", "test-versions"],
    actions: ["impersonation", "proof"],
  },
  {
    id: "test-versions",
    topic: "proof",
    title: "Test published versions, drafts and mirrored forms",
    summary:
      "Choose which document version and which form Live proof, Test this form and impersonation load.",
    keywords: [
      "test this form",
      "version",
      "draft",
      "revision",
      "mirror",
      "form from",
      "default",
      "impersonation",
    ],
    steps: [
      [
        "Choose the document",
        "In Live proof, Document version lists Latest published, each published version and, for accounts that may manage documents, the Current draft and retained draft revisions from History. Changing it resets the form inputs.",
      ],
      [
        "Mirror a different form",
        "Form from keeps Same as document, or takes the form (fields, groups, tabs, rules, parts, datasets and form layout settings) from another version or draft while the pages, styles and outputs stay with the chosen document. Variables the artwork needs but that form lacks are kept from the document. Use it to try a new form against the artwork customers already have, or the reverse.",
      ],
      [
        "Open a test window",
        "Test this form opens the same choice in a new window with the user picker, exactly like Impersonation. The window has its own Document version and Form from controls, so you can switch without returning to the workspace.",
      ],
      [
        "Set the project default",
        "Make project default stores the selection with the project (it is saved as a new draft revision, so save your other changes first). Live proof, Test this form and Impersonation start from it; a test window or an integration can still choose another. If a stored draft revision is later pruned, the latest published version is used instead.",
      ],
    ],
    notes: [
      "Only a single published version (document and form from the same version) can generate documents or save proofs. Drafts and mirrored forms are previews: publish to generate or save.",
      "Drafts stay limited to accounts that may manage documents. The server checks the testSelection on each request and refuses drafts to other accounts; they only see published versions.",
      "Integrations pass testSelection: { document, form? } (published, current, version:<id> or revision:<id>) instead of versionId on proofing requests; see the API reference.",
    ],
    related: ["impersonation", "live-proof", "save-versions"],
    actions: ["proof", "impersonation"],
  },
  {
    id: "maps",
    topic: "proof",
    title: "Find a location and create a map",
    summary:
      "Create exact-size map imagery that stays with proof and export values.",
    keywords: [
      "Austin",
      "Chicago",
      "Seattle",
      "location",
      "map",
      "resolution",
      "Mapbox",
      "geographic",
      "provider",
    ],
    steps: [
      [
        "Explore the map starter",
        "Create a Location invitation from New document, then open Live proof.",
      ],
      [
        "Choose a location",
        "Read the configured provider's notice, search an address, select a result and choose Use this map. The offline starter recognizes Austin, Chicago and Seattle.",
      ],
      [
        "Compare part sizes",
        "Switch to the High-resolution map part to create a larger image. Review the result, save a proof and generate the selected output.",
      ],
    ],
    notes: [
      "The offline demo produces schematic illustrations. A server can configure Mapbox geographic lookup and imagery, or a host can inject another provider. Saved values retain the selected image and location provenance. Geographic lookup is hidden when no provider is available; image uploads remain usable.",
    ],
    related: ["assets", "part-overrides", "embedding"],
    actions: ["new", "proof"],
  },
  {
    id: "output-profiles",
    topic: "proof",
    title: "Configure formats, resolution and CMYK",
    summary:
      "Create document output profiles for screen, image and print delivery.",
    keywords: [
      "PDF",
      "SVG",
      "PNG",
      "JPEG",
      "TIFF",
      "RGB",
      "CMYK",
      "DPI",
      "watermark",
      "PDF/X",
      "ICC",
      "printer profile",
    ],
    steps: [
      [
        "Choose a profile",
        "Open Configuration → Output profiles. Global output profiles are reusable across projects. Create or edit a global version, select a project stage, then Assign to selected stage and save the project draft. Global edits leave existing assigned versions unchanged. Use up to four stages: Preview / Proof (96 DPI), Proof Download (72 DPI), High Res (300 DPI) and Print (600 DPI). Defaults are editable. Set up four output stages converts older documents and retains every original option as a reusable output preset. Select Output settings preset to reuse one.",
      ],
      [
        "Set quality and permissions",
        "Set Resolution (DPI), JPEG quality when relevant, watermark, Minimum effective DPI and the Low-resolution images policy. Choose Allow viewing and Allow downloading.",
      ],
      [
        "Pin your printer's ICC profile",
        "For CMYK output, use Upload printer ICC profile to select a CMYK printer ICC v2/v4 file up to 4 MiB. Its exact bytes stay with this output profile, reusable library versions and render snapshots. Remove printer profile restores the generic conversion.",
      ],
      [
        "Configure production geometry",
        "Add page bleed, slug, crop marks or sheet arrangements where required. Open Profile bleed and marks and enable Override page production settings to give this profile its own bleed, slug, clipping and marks for every logical page. Disable the override to inherit page settings. Include page bleed, slug and marks must be enabled to use production geometry. PDF artwork bounds selects ArtBox metadata: inherit the page, use media/trim/bleed, or apply a custom trim-relative rectangle that fits exported media. It does not crop visible artwork. Profile bounds apply to imposed sheets. Combine page marks and Combine bleed marks draw each mark set as one outline path followed by one ink path, preserving its dimensions. Absolute PDF text positions reduces coordinate rounding in unrotated text when matching a PDF reference; rotated text retains its frame placement. PDF RGB channel precision chooses 3–6 decimal places for drawing colors when matching a reference. Publishing imports retain named source jobs as reusable output presets. Select a Page bleed profile or inherit each page. Save bleed preset retains project bleed settings. Save output preset creates and assigns a global output definition. The global library supports Create global output profile, Edit global profile, and Save and assign to project; previously project-owned presets can Publish global copy. Save the draft.",
      ],
      [
        "Generate from a proof",
        "Select that profile in Live proof or Batch settings. Review the artifact information and use its allowed download/view controls.",
      ],
    ],
    options: [
      [
        "Supported output",
        "PDF, SVG, PNG, JPEG and TIFF. CMYK is supported for PDF, TIFF and JPEG; use RGB for screen previews.",
      ],
      [
        "Raster resolution",
        "36–1200 DPI within the 12,000-pixel-per-axis and 40-megapixel page bounds. Proof Download PDF rasterizes each page at the configured DPI, limiting its actual detail. Production PDF/SVG vector geometry is not made higher resolution by increasing DPI.",
      ],
      [
        "Multiple pages",
        "Multipage raster/SVG deliveries contain numbered files and a manifest in a ZIP.",
      ],
      [
        "Original imported PDF artwork",
        "Preserve imported PDF colors and vectors retains the placed source colors in PDF output without CMYK conversion. Native content follows the RGB target; placed sources may still contain CMYK paint. This option does not guarantee an all-RGB or PDF/X file. Turn it off or choose CMYK to convert the raster preview with the output profile.",
      ],
    ],
    notes: [
      "An uploaded printer ICC profile drives vector/text and image separation together; PDF embeds the printing condition and TIFF/JPEG retain the profile. Without an uploaded profile, vector CMYK uses a device-color formula and images use the generic profile. Actual printer/paper matching and independent PDF/X conformance qualification remain separate requirements.",
    ],
    related: ["sheets", "transparent-output", "troubleshooting"],
    actions: ["outputs", "proof"],
  },
  {
    id: "transparent-output",
    topic: "proof",
    title: "Export with no background or fixed page size",
    summary:
      "Make transparent artwork or calculate a finite output size from visible content.",
    keywords: [
      "alpha",
      "transparent",
      "auto-size",
      "no size",
      "content-sized",
      "background",
      "signature",
    ],
    steps: [
      [
        "Remove the paper background",
        "In Document designer → Page settings, enable Transparent background.",
      ],
      [
        "Let content define the bounds",
        "Enable Auto-size to content for the supported dimensions. Review the resulting width and height as visible artwork changes.",
      ],
      [
        "Choose a compatible export",
        "Use PNG or SVG for alpha artwork. PDF can omit a painted page background. Review the actual output after applying any document frame or sheet layout.",
      ],
    ],
    notes: [
      "JPEG and CMYK raster exports flatten transparency to white. PDF media always has rectangular finite bounds, even when the artwork has no fixed authoring size or uses a silhouette frame.",
    ],
    related: ["base-shape", "output-profiles", "layers"],
    actions: ["designer", "outputs"],
  },
  {
    id: "sheets",
    topic: "proof",
    title: "Arrange copies on production sheets",
    summary:
      "Pack document pages onto sheets with bleed, crop marks and duplex alignment.",
    keywords: [
      "imposition",
      "16-up",
      "sheet",
      "copies",
      "duplex",
      "rotation",
      "bleed",
      "crop marks",
    ],
    steps: [
      [
        "Enable sheet layout",
        "In Configuration → Sheets & paper, create a reusable sheet preset. In Output profiles, assign it to High Res or Print, or configure Arrange copies on a sheet and use Save sheet preset. Preview and Proof Download use individual products.",
      ],
      [
        "Set the packing",
        "Choose sheet dimensions in points or a standard size, margins, exact or automatic packing, page selection, copy count, rotation, spacing and alignment. Record material, cost per physical sheet, currency, paper weight (GSM), caliper, color, coating, finishing, grain, printed sides, process, press, supplier, stock SKU, recycled content and sustainability. Configure bleed and crop marks as needed.",
      ],
      [
        "Review both sides",
        "Live proof always shows the individual product. Download the High Res or Print artifact to inspect imposed sheets, partial sheets and paired front/back placement. The summary estimates material cost from physical sheets and stock cost; it excludes press time, finishing, spoilage and tax.",
      ],
    ],
    options: [
      [
        "Starter example",
        "The business-card starter includes Business card 16-up for a working duplex sheet configuration.",
      ],
      [
        "Page vs sheet",
        "Page production settings describe each item's trim/bleed footprint; sheet settings describe how those items are packed onto the final media. Bleed is hidden by default in the designer and Live proof. Show bleed changes the view only. Output profiles can disable that checkbox or set its default visibility.",
      ],
    ],
    related: ["output-profiles", "base-shape", "bulk-batches"],
    actions: ["outputs", "proof"],
  },
  {
    id: "bulk-template",
    topic: "bulk",
    title: "Download a form-matched bulk template",
    summary:
      "Get the right CSV columns before entering one row for each document you want to generate.",
    keywords: [
      "download template",
      "CSV",
      "required",
      "choice",
      "header",
      "part",
      "spreadsheet",
    ],
    steps: [
      [
        "Choose the part first",
        "Open Bulk generation and select Part before uploading. The effective form variables determine the template.",
      ],
      [
        "Read the field guide",
        "Expand Template field guide to see column names, form labels, required/conditional fields and accepted choice values.",
      ],
      [
        "Download and fill",
        "Choose Download template. Add one data row per document to the UTF-8 CSV. Use direct HTTP/HTTPS image URLs or managed asset IDs for image columns, choice values, true/false for simple checkboxes, and semicolons for multi-choice checkbox values.",
      ],
      [
        "Import the completed file",
        "Upload the CSV, review its suggested mappings, and Confirm column mappings. Image URLs are downloaded into the asset library before preview and generation. Failed URLs stay in review while valid records can proceed.",
      ],
    ],
    notes: [
      "Templates contain headers only, without personal profile data or defaults. Optional columns can be removed to keep inherited values; an empty mapped cell is an explicit empty submission.",
      "Read-only, system, generated, lookup and structured/map fields are excluded. Editable image fields and conditional scalar fields are included. Download is disabled if no eligible fields exist or the source would exceed 500 columns.",
    ],
    related: ["bulk-import", "part-overrides", "bulk-batches"],
    actions: ["bulk"],
  },
  {
    id: "bulk-import",
    topic: "bulk",
    title: "Import, map and validate bulk data",
    summary:
      "Review spreadsheet parsing and field mappings before generating any records.",
    keywords: [
      "CSV",
      "TSV",
      "XLSX",
      "delimiter",
      "encoding",
      "header row",
      "worksheet",
      "formula",
      "invalid rows",
    ],
    steps: [
      [
        "Import the source",
        "Upload CSV, TSV, delimited text or XLSX. Select a worksheet when the workbook contains more than one.",
      ],
      [
        "Review parsing",
        "Set File format, Text encoding, Header row and Text delimiter if auto-detection needs correction. Reparse source after changing these options.",
      ],
      [
        "Confirm the mappings",
        "Map each useful source column to one compatible variable, or choose Skip column. Exact template headers map automatically; ambiguous source headers require review.",
      ],
      [
        "Inspect every record",
        "Confirm column mappings, browse each proof and its pages, and review Ready and Needs attention counts. Generate valid records submits only valid rows.",
      ],
    ],
    options: [
      [
        "Limits",
        "10 MB source files, up to 10,000 rows, 500 columns and 20 worksheets within parser expansion/cell bounds.",
      ],
      [
        "Safe workbook handling",
        "Formulas are not executed and external workbook links are not fetched. Supported cached cell values are used.",
      ],
    ],
    notes: [
      "Changing the part clears column assignments and requires renewed confirmation. Changing parser options requires reparsing before generation.",
    ],
    related: ["bulk-template", "bulk-batches", "datasets"],
    actions: ["bulk"],
  },
  {
    id: "bulk-batches",
    topic: "bulk",
    title: "Resume batches, retry rows and download results",
    summary:
      "Track immutable row submissions and recover completed work without resubmitting everything.",
    keywords: [
      "saved batch",
      "ZIP",
      "retry",
      "cancel",
      "recover",
      "restart",
      "row pages",
    ],
    steps: [
      [
        "Monitor the batch",
        "After Generate valid records, inspect each row's status and artifact. Browse record and proof-page controls to review successes and failures.",
      ],
      [
        "Recover a review",
        "Choose Saved batch to restore the server snapshot. The original browser cache also retains invalid review rows and per-row page positions.",
      ],
      [
        "Retry selectively",
        "Retry only eligible retryable failures, or cancel unfinished work. Successful artifacts remain available.",
      ],
      [
        "Download completed work",
        "Use Download this record for an individual result or the batch ZIP control for allowed completed artifacts. Each file is checked against its saved size, type and SHA-256 before delivery.",
      ],
    ],
    notes: [
      "JavaScript hosts can embed the same BulkGeneration panel or proof-bulk-generation element with authorized batch/context services, preview byte loaders and delivery callbacks; an API is optional. Batches retain the document, part, mapping and accepted values used at creation. Later editor changes do not rewrite the batch. Another browser can recover accepted rows; invalid source rows remain local to the original browser.",
      "Restart recovery requires file-backed or PostgreSQL job storage. Shared PostgreSQL workers recover expired leases without interrupting live peers. Default in-memory jobs and batches belong to the current API session.",
    ],
    related: ["bulk-template", "bulk-import", "render-jobs"],
    actions: ["bulk", "operations"],
  },
  {
    id: "instance-access",
    topic: "workspace",
    title: "Set up an instance and sign in as an operator",
    summary:
      "Configure native providers and use separate platform access in instance setup mode.",
    keywords: [
      "bootstrap",
      "operator",
      "PostgreSQL",
      "cookie",
      "recovery",
      "instance",
      "password",
    ],
    steps: [
      [
        "Enable configured startup",
        "The API administrator enables PROOF_INSTANCE_SETUP_MODE and supplies a private bootstrap token file. The default database-free demo remains available without this mode.",
      ],
      [
        "Complete the six-step wizard",
        "Open the canonical public address. Configure ownership, PostgreSQL, independent object/artifact storage, identity and runtime providers. Generate distinct login and request keys, validate connections, then create the instance. Choose embedded coordination for evaluation or Redis dispatch with its private URL and optional CA certificate. Production requires authenticated TLS Redis, HTTPS identity/origins, S3, trusted proxies and PostgreSQL TLS.",
      ],
      [
        "Keep recovery inputs private",
        "Only non-secret fields resume in the tab. Passwords, keys, URLs containing credentials and bootstrap tokens clear on reload. Download private setup file explicitly if an identical interrupted-setup retry is needed; that file contains credentials.",
      ],
      [
        "Use platform access",
        "Sign in at /operator/login for platform status and read-only Render activity. Change password revokes other sessions; recovery accounts must rotate their temporary password before access. Log out when finished.",
      ],
      [
        "Edit saved configuration",
        "The instance owner opens Instance configuration from Platform status. Adjust deployment, renderer, queue, identity/context, order delivery or provider credentials; retain existing secrets or enter replacements. Ordering requires HTTP tenant identity and a successful read-only host status check; validation never submits an order. Validate and download the private recovery file, then confirm that API work is drained and apply. Restart every API before reopening traffic. If publication is interrupted, retry the downloaded file with setup:instance --file, --edit and --yes. Storage moves and login/rate-key rotation require separate procedures.",
      ],
      [
        "Enter temporary tenant support access",
        "Open Tenant support access from Platform status. An explicitly granted account supplies the tenant ID, a reason, a five-to-fifteen-minute duration and its current password. The checked tenant workspace opens with a persistent banner naming the operator, effective administrator, tenant and expiry. Stop support access returns to platform status without logging out; Log out ends the parent session. Expiry, password changes and revoked grants end access across tabs and queued work. The instance owner can manage operator grants with fresh password confirmation; changing a grant ends that operator's sessions.",
      ],
      [
        "Rotate instance security keys offline",
        "Stop API traffic and probes, drain jobs and request/login budgets, and back up the private configuration and secret directories. Prepare a private file with the current expected revision, owner credentials and two distinct fresh keys. Run rotate:instance-keys with --file and --yes, using --validate-only first. The command atomically changes both key authorities, revokes operator sessions and publishes a new revision. Restart every API and sign in again. Retain the file for an identical interrupted-publication retry; spent budgets are never cleared to make rotation succeed.",
      ],
    ],
    notes: [
      "Operator sign-in alone grants no tenant membership. Only an explicitly granted, password-confirmed support context can open its selected tenant. The live-proof impersonation demo is a separate document-preview workflow. Support actions retain operator and effective attribution, and support contexts never become permanent memberships.",
      "Configured mode opens the shared tenant workspace using the verified host identity. Owner-authenticated browser and CLI editing preserve immutable revision history. Recovery downloads contain credentials and must be kept private; the normal editor hides existing secret values and mounted paths. External SSO and production deployment qualification remain open.",
    ],
    related: ["workspace-setup", "access", "embedding"],
    actions: ["integrations"],
  },
  {
    id: "shared-workspace",
    topic: "workspace",
    title: "Connect to a shared tenant workspace",
    summary:
      "Use authenticated API storage, published versions and private document resources.",
    keywords: [
      "host token",
      "native",
      "disconnect",
      "conflict",
      "publication",
      "tenant",
    ],
    steps: [
      [
        "Sign in to your workspace",
        "The normal application uses its account sign-in and top site/company selector. An explicitly host-managed deployment instead accepts its host access token. Platform operator access is separate.",
      ],
      [
        "Prepare the workspace",
        "An administrator can name the workspace and create starter defaults before creating a document. Empty shared workspaces do not receive the demo's local documents.",
      ],
      [
        "Save shared drafts",
        "Documents, history and resources use API storage. A revision conflict preserves your unsaved draft; Reload saved document explicitly replaces it with the latest saved copy.",
      ],
      [
        "Select a publication",
        "Publish an immutable version and select it in Live proof or Bulk generation. Authors can also preview the current draft, a retained draft revision or a mirrored form; generation and saved proofs require a single published version.",
      ],
      [
        "Disconnect when finished",
        "In application-owned mode, the sign-out icon beside your name at the bottom of the navigation revokes your account session. Host-managed Disconnect workspace clears the connection and private UI state; reconnect using host access. Form values, bulk uploads and unsaved personal profiles stay in memory.",
      ],
    ],
    notes: [
      "Available tools follow tenant permissions. The server checks every operation; changed or revoked access clears the old workspace scope.",
      "Private fonts, images and downloads use authenticated requests. Impersonation offers eligible real users or Nobody and opens the selected publication with a modal form and an in-memory new-tab handoff. The selected user supplies profile data; authorization and ownership stay with your current account. During operator support access, the checked context and visible banner remain; stopping or revoking it closes preview access.",
      "Disconnecting does not revoke an external host token. Reusable library entries are shared records; personal form profiles last only for this connection.",
    ],
    related: [
      "instance-access",
      "workspace-setup",
      "save-versions",
      "bulk-batches",
    ],
    actions: ["setup", "proof", "bulk"],
  },
  {
    id: "workspace-setup",
    topic: "workspace",
    title: "Complete workspace setup and readiness",
    summary:
      "Establish real defaults and understand the workspace setup warning.",
    keywords: [
      "setup",
      "readiness",
      "warning",
      "skip",
      "restore",
      "tenant",
      "defaults",
    ],
    steps: [
      [
        "Open the checklist",
        "Use Workspace setup to review the server-authored readiness steps and name the workspace.",
      ],
      [
        "Create and pin defaults",
        "Use the setup controls to establish real page/output resources and review required font/resource readiness. Save the tenant policy when changing defaults.",
      ],
      [
        "Handle optional steps",
        "Skip optional steps when appropriate and restore them later. Revisit the checklist when required resources are removed or archived.",
      ],
    ],
    notes: [
      "Setup warnings describe workspace readiness; ordinary document navigation remains available. Administrative changes require the corresponding tenant permission.",
    ],
    related: ["defaults", "access", "managed-fonts"],
    actions: ["setup", "configuration"],
  },
  {
    id: "access",
    topic: "workspace",
    title: "Assign users and roles",
    summary:
      "Manage host-authenticated subjects with scoped roles and explicit revocations.",
    keywords: [
      "permissions",
      "Editor",
      "Tester",
      "Asset Manager",
      "Tenant Administrator",
      "membership",
      "access denied",
    ],
    steps: [
      [
        "Choose a subject",
        "Open Users & roles and select the host-authenticated subject whose tenant access you need to manage.",
      ],
      [
        "Assign the role set",
        "Choose Editor, Tester, Asset Manager and/or Tenant Administrator as appropriate. Multiple role grants combine their capabilities; explicit revocations are enforced.",
      ],
      [
        "Save a current revision",
        "Review the assignment and save. If another administrator changed it first, refresh the current record and review before saving again.",
      ],
    ],
    notes: [
      "Last-administrator protection prevents removing the final administrator. The demo API runs as a demo administrator; a production deployment supplies its own verified identity and authorization integration. Impersonation's real-user preview does not grant roles or create memberships.",
    ],
    related: ["requests-audit", "workspace-setup", "impersonation"],
    actions: ["access"],
  },
  {
    id: "requests-audit",
    topic: "workspace",
    title: "Review account requests and security events",
    summary:
      "Approve explicit roles, reject with context, and inspect safe denial records.",
    keywords: [
      "account requests",
      "approve",
      "reject",
      "audit",
      "security",
      "rate limit",
      "stale",
    ],
    steps: [
      [
        "Open the request inbox",
        "In Users & roles → Account requests, inspect a host-provided request. Add demo request creates a local server-owned sample.",
      ],
      [
        "Record a decision",
        "Select explicit roles before approval, or enter a rejection reason. If the review became stale, refresh and decide against the latest request revision.",
      ],
      [
        "Inspect security events",
        "Open Security audit under Users & roles to review safe denial records, required capabilities and request-limit policies. Use older-history controls to inspect earlier entries.",
      ],
    ],
    notes: [
      "Decisions and role changes retain audits. A rate-limit response should be retried after its indicated delay; repeatedly clicking does not bypass the limit. Account requests are supplied by a host integration in production.",
    ],
    related: ["access", "render-jobs", "troubleshooting"],
    actions: ["access"],
  },
  {
    id: "render-jobs",
    topic: "workspace",
    title: "Inspect, cancel and retry render jobs",
    summary:
      "Use attempt history and reason-required operations to recover work safely.",
    keywords: [
      "job",
      "attempt",
      "probation",
      "checksum",
      "cancel",
      "retry",
      "failed",
      "quarantined",
    ],
    steps: [
      [
        "Inspect the job",
        "Open Render operations to review job state, events, immutable snapshots and attempt history.",
      ],
      [
        "Choose an eligible operation",
        "Enter a reason to cancel unfinished work or retry an eligible failed attempt. Refresh stale state before sending another operation.",
      ],
      [
        "Review retry limits",
        "After three attempts, a tenant administrator can explicitly grant one final probation attempt when the job is eligible.",
      ],
      [
        "Verify recovery storage",
        "Use file-backed or PostgreSQL job storage for restart recovery. PostgreSQL shares jobs, attempts, bulk progress and artifact associations across API processes. Completed artifact downloads are checked against their recorded byte count and checksum.",
      ],
    ],
    notes: [
      "Memory mode retains jobs for the current API session. Distributed dispatch, shared production storage and operational recovery qualification are separate deployment work.",
    ],
    related: ["bulk-batches", "output-profiles", "troubleshooting"],
    actions: ["operations"],
  },
  {
    id: "embedding",
    topic: "integration",
    title: "Embed forms, proofing and authoring tools",
    summary:
      "Use the independent packages or plain-HTML components with host-supplied services.",
    keywords: [
      "React",
      "SDK",
      "Web Component",
      "proof-form",
      "proof-authoring",
      "authentication",
      "private images",
      "modal",
      "width",
      "height",
      "plug and play",
      "API",
      "font",
      "asset",
      "map",
    ],
    steps: [
      [
        "Configure B2B access",
        "Open Access & tokens as a workspace administrator. Create a client with maximum permissions, token lifetime and allowed website origins, then save the one-time secret on your backend. Exchange client credentials at /api/oauth/token for an expiring permission subset. Enable Use a B2B access token when copying an embed. Permissions apply across this workspace; client secrets stay out of the browser. Rotate or revoke credentials and individual tokens from the same page.",
      ],
      [
        "Explore the integration sandbox",
        "Open Integrations and choose the form or Embed authoring sandbox. Sandbox edits stay separate from the workspace draft.",
      ],
      [
        "Choose your integration surface",
        "Use controlled React components, independent TypeScript packages, REST, <proof-form> or <proof-authoring>. Authoring supports configuration, designer, output-setting and summary modes. ProjectExport and <proof-project-export> accept a project, format/part options and host report/export callbacks. Use proof:package to receive the ZIP in your host, with optional browser download. DocumentImport and <proof-document-import> accept injected import services, an optional trusted JSON tenant and an async host acceptance callback. proof:import delivers the accepted project and report; the host owns saving. Cancellation and setup replacement fence stale results. An API connection is enabled only when explicitly configured.",
      ],
      [
        "Embed revision review",
        "Use ProjectRevisionReview or proof-revision-review with current and incoming projects, preview loaders, a whole-document resource check and an onApply acceptance callback. An optional baseline keeps the draft captured before import. The component preserves identity and sparse overrides, waits for acceptance and cancels stale work on replacement or disconnect. The host owns persistence and revision checks; an API adapter is enabled only when explicitly configured.",
      ],
      [
        "Configure the embedded form",
        "In Embed a form, select Inline or Modal, set width/height and maximum dimensions, change the modal title and launch label, and toggle the document preview. Try the live component and copy the generated snippet with your API address. Numeric sizes mean pixels; CSS lengths and auto are supported. Modal forms fit the viewport, close with Escape or the backdrop, and keep entered values when reopened.",
      ],
      [
        "Provide host services",
        "Supply the project and values, listen for change events, and persist validated definitions in your repository. Inject assets, managed fonts, image/map services and trusted profile/order context as required.",
      ],
      [
        "Choose appearance and permissions",
        "In Embed a form or Embed authoring, choose the embed theme, brand colours, font, density and corners. Allow or disable editing, proof preview and summary export. Authoring also lets you enable individual editor flows. Try the live preview, remember the choices in this browser for this project, then copy the configured setup code into your host. Server roles still govern access and saving.",
      ],
    ],
    notes: [
      "An explicitly configured API URL can provide transport; plain controlled components can also work with injected local services. A production host must enforce verified identity and permissions. Client-submitted profile objects are not trusted server identity.",
      "Set the headers property before connecting API-backed components to load private documents, images and fonts. Credential changes and reconnects clear the old API session before reauthorization. Use reload() for external session changes. Host-owned projects can inject loadFont and image loadAsset callbacks without the application API.",
    ],
    related: [
      "legacy-handoff",
      "mapping",
      "managed-fonts",
      "integration-design-mode",
    ],
    actions: ["integrations"],
  },
  {
    id: "integration-design-mode",
    topic: "integration",
    title: "Configure integrations with Design Mode",
    summary:
      "Turn editor features on or off, replace wording, preview a style and generate the implementation source.",
    keywords: [
      "Design Mode",
      "design-mode",
      "configure integration",
      "text overrides",
      "label replacements",
      "generate source",
      "Use Source Input",
      "translate",
      "part SKU",
      "profile callback",
      "integration style",
    ],
    steps: [
      [
        "Turn on Design Mode",
        "Add the design-mode attribute (or options.designMode = true) to any integration element. A Design mode badge and a gear appear, and configurable regions are outlined. Design Mode is off by default and is meant for developers; server authorization still controls access.",
      ],
      [
        "Choose features",
        "Select the gear (Configure integration). Each editor area and capability has its own switch: statements, rules, validation, formatting, dynamic sources, conditional visibility, part overrides, form display, calculated values, form layout, Generate Form, View Form, global forms, the Data & presets tab, Advanced setup, JSON configuration, field filters and Use Source Input. New optional capabilities are off unless you turn them on.",
      ],
      [
        "Replace wording",
        "Text overrides lists each catalog entry, such as the editor heading and layout actions. Label replacements changes any other visible label, button, toggle or dialog title: enter one Original = Replacement per line. Only exact matches change, and blank entries keep the default wording.",
      ],
      [
        "Preview a style",
        "Integration style applies a preset to the embedded pieces only. The Propago preset appears only for accounts permitted to use the ESM tables (Propago or Cormier domains). Explicit theme colours still take precedence.",
      ],
      [
        "Connect host data",
        "With Use Source Input on, a field's dataset or asset source is requested from the parent page through the proof:source-request event or a sourceInputHandler callback. Profile, part-override and part SKU callbacks follow the same contract: return the rows directly or { data, translate }. Translation runs before the data is validated and used. Loading, cancellation, timeouts and invalid data are reported without changing existing values.",
      ],
      [
        "Generate the implementation",
        "Choose Generate source to copy the browser module, element markup and current configuration, plus stubs for each host hook. Generated source never includes credentials or profile values; supply authenticated data and callbacks in your host.",
      ],
    ],
    notes: [
      "The full option table, request shapes and JavaScript examples are in the integration editor options and host-provided source inputs references.",
    ],
    related: ["embedding", "field-policies", "forms"],
    actions: ["integrations"],
  },
  {
    id: "api-reference",
    topic: "integration",
    title: "Integrate through the native API contract",
    summary:
      "Browse the installed API, download OpenAPI JSON and use an independent HTTP client.",
    keywords: [
      "OpenAPI",
      "REST",
      "Python",
      "HTTP",
      "contract",
      "schemas",
      "client generator",
      "API & packages",
    ],
    steps: [
      [
        "Discover the installed API",
        "Open Integrations → API & packages. Search methods, paths or operation names, then inspect request bodies, response schemas, path/query/header parameters and required capabilities. The page identifies configured or database-free demo mode.",
      ],
      [
        "Download the contract",
        "Download OpenAPI JSON for your HTTP tooling. Configured generation needs an immutable published version; the demo also accepts inline native snapshots. Shared schemas describe typed values, multiple datasets, sparse part overrides, output profiles, jobs and retained packages.",
      ],
      [
        "Follow the host workflow",
        "Create or save a document, publish it, resolve required host context, queue generation, poll its job and verify the downloaded artifact checksum. Keep the same idempotency key after an uncertain approved-package response. Python's standard-library example exercises this flow without the TypeScript SDK.",
      ],
      [
        "Keep authority on the server",
        "Use your verified host authentication and selected API origin. Request fields do not grant a tenant or role. The command examples contain placeholders, and the reference never executes requests or copies credentials. Cross-field and current-authority checks still run on the server.",
      ],
    ],
    notes: [
      "The contract covers native tenant operations. Operator sign-in and instance administration use separate interfaces. External deployment, host order commits and PDF/X qualification remain separate work.",
    ],
    related: ["embedding", "legacy-handoff", "render-jobs", "bulk-batches"],
    actions: ["integrations"],
  },
  {
    id: "legacy-json-path",
    topic: "integration",
    title: "Map nested directory responses with JSONPath",
    summary:
      "Select nested values, filtered choices and image references through the independent host mapper.",
    keywords: [
      "JSONPath",
      "filter",
      "slice",
      "recursive",
      "descendant",
      "directory",
      "imagePath",
      "valuePath",
      "labelPath",
    ],
    steps: [
      [
        "Configure the host source",
        "In the trusted source adapter, configure each variable's JSON_PATH and optional success path. Native document variables bind to returned context keys; source URLs, credentials and SQL identity mappings stay on the host.",
      ],
      [
        "Select the required records",
        "Use $.rows[?(@.active == true)] for active records, $.rows[-1] for the last record, $.rows[::-1] to reverse an array, or $..code for descendant values. Unions retain their order and duplicates. Filters use strict JSON types and existence tests; @.active == true tests a boolean, while @.active tests whether the member exists.",
      ],
      [
        "Map values and images",
        "Set per-choice valuePath, labelPath and imagePath relative to each selected row. Each needs one match. The complete mapping is checked before image ingestion; selected images become managed assets. Missing or ambiguous values and exhausted query limits block fresh context instead of reusing old choices.",
      ],
      [
        "Review and retain",
        "Use live proof and review the generated output after changing source paths or criteria. Accepted jobs retain their reviewed context. The independent path reader, HTTP provider and image mapper use the same bounded parser.",
      ],
    ],
    notes: [
      "The parser supports boolean/comparison filters, escaped names, descendants, slices, unions and typed length/count/value functions. Imported JavaScript, arithmetic/script indices, regex extensions and unknown function calls require an explicit host conversion.",
      "Paths and result counts are bounded, and one traversal budget covers a complete response mapping. The query never invokes a host method or reads inherited properties. Source definitions and query text survive legacy table exchange.",
    ],
    related: ["legacy-handoff"],
    actions: ["integrations"],
  },
  {
    id: "legacy-handoff",
    topic: "integration",
    title: "Transform legacy data and prepare order handoff",
    summary:
      "Round-trip the supplied table structure and export reviewed customization for another system.",
    keywords: [
      "legacy",
      "ESM",
      "SQL",
      "transformer",
      "JSON",
      "Pageflex",
      "XVP",
      "order",
      "handoff",
    ],
    steps: [
      [
        "Review the adapter",
        "In Integrations, import or export the legacy ESM table envelope. Review transformer warnings and identity mappings before using it in a host system.",
      ],
      [
        "Keep the native model",
        "The application can use its own canonical model while the transformer preserves unknown columns/tables and converts to and from the supplied structure.",
      ],
      [
        "Prepare customization handoff",
        "Use the order handoff controls to restore typed values, review final order substitutions and download the Pageflex XVP handoff.",
      ],
      [
        "Retain an approved PDF",
        "Generate a downloadable PDF in Live proof, approve its content and enter initials. Retain approved proof saves its exact reviewed bytes, customization and XVP. Retrieve the ZIP under Integrations → Order handoff → Retained proof packages, even after later edits or reload. Configured mode requires the matching published version; temporary server storage is labelled.",
      ],
      [
        "Submit to your ordering system",
        "Configure ordering in the saved-instance Runtime providers step or the owner's Instance configuration page, or inject a host adapter. Setup validates read-only host status without placing an order. Retained packages then show Send to your ordering system. Enter its cart/order reference and submit explicitly. Confirmation shows the host's order and line receipt. After an uncertain response, retry the same package and reference to recover that transaction rather than create another submission.",
      ],
    ],
    notes: [
      "Part rows represent explicit overrides; inherited values are computed at runtime. Retained approval keeps IS_COMPLETE false even after a separate host receipt. The default demo prepares artifacts; demo:orders adds a fictional memory-only order host. A real host must authorize and atomically persist/deduplicate its transaction. Unresolved final order values require a new render with trusted context.",
    ],
    related: ["embedding", "part-overrides", "save-versions"],
    actions: ["integrations"],
  },
  {
    id: "troubleshooting",
    topic: "start",
    title: "Resolve common proof and export problems",
    summary:
      "Find the next check when values, fonts, images, downloads or imports do not match expectations.",
    keywords: [
      "error",
      "failed",
      "missing",
      "loading",
      "RATE_LIMITED",
      "CONTEXT_CHANGED",
      "overflow",
      "font",
      "download",
      "unsupported",
    ],
    steps: [
      [
        "Check effective values",
        "Confirm the current document, part, profile and output. Reset an unintended override rather than copying the main project's settings into a part. Hidden fields and read-only sources can affect what is submitted.",
      ],
      [
        "Check readiness",
        "Wait for managed fonts, images and profile values to load. Refresh failed profile values; refresh and review after CONTEXT_CHANGED. For RATE_LIMITED, wait for the server's retry delay.",
      ],
      [
        "Check layout and output",
        "Inspect overflow and image-DPI warnings. Choose a lower raster DPI or smaller media when pixel bounds are exceeded. Check the output profile's viewing/downloading permissions if an artifact action is absent.",
      ],
      [
        "Check the import or job report",
        "Replace missing image links and substituted fonts after import. Review Render operations for failed attempts and eligible retries. Preserve the original document for appearance comparison.",
      ],
    ],
    notes: [
      "RGB preview is not a calibrated CMYK press proof. Exporting CMYK does not validate PDF/X. File-backed storage is required if API job history must survive restart.",
    ],
    related: ["output-profiles", "managed-fonts", "render-jobs"],
    actions: ["proof", "operations"],
  },
];

export const quickStartGuides = [
  "first-document",
  "import-document",
  "bulk-template",
];
export function visibleHelpGuides(esmAllowed: boolean): HelpGuide[] {
  if (esmAllowed) return helpGuides;
  const containsEsm = (text: string) => /\besm\b/i.test(text);
  return helpGuides
    .filter((guide) => guide.id !== "legacy-handoff")
    .map((guide) => ({
      ...guide,
      keywords: guide.keywords.filter((text) => !containsEsm(text)),
      steps: guide.steps.filter((row) => !row.some(containsEsm)),
      options: guide.options?.filter((row) => !row.some(containsEsm)),
      notes: guide.notes?.filter((text) => !containsEsm(text)),
      related: guide.related.filter((id) => id !== "legacy-handoff"),
    }));
}
export function searchHelp(
  query: string,
  topic: TopicId | "all" = "all",
  guides: HelpGuide[] = helpGuides,
) {
  const terms = query
    .normalize("NFKC")
    .toLocaleLowerCase()
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  return guides.filter((guide) => {
    if (topic !== "all" && guide.topic !== topic) return false;
    const text = [
      guide.title,
      guide.summary,
      helpTopics.find((item) => item.id === guide.topic)!.label,
      ...guide.keywords,
      ...guide.steps.flat(),
      ...(guide.options || []).flat(),
      ...(guide.notes || []),
    ]
      .join(" ")
      .normalize("NFKC")
      .toLocaleLowerCase();
    return terms.every((term) => text.includes(term));
  });
}
